/* ═══════════════════════════════════════════════════════════════════
   SIMSPACE BUILDER v3.0 — Room-first configurator
   ═══════════════════════════════════════════════════════════════════
   Based on v2.1 (Apple-style step configurator + Klaviyo).

   New in v3.0
   - Step 1 "Your room": room type + width / depth / ceiling (m or ft).
     Enclosure and launch monitor cards get fit badges from those numbers.
   - Build state is written to the URL (?build=...) and localStorage,
     and restored on load. Email-quote links now open the same build.
   - "Your build" summary rail is visible from step 1 (desktop left
     column; mobile bottom sheet from the sticky bar).
   - Included / not included lines and an estimated software-per-year
     line, both configurable from JSON blocks.
   - Compatibility badges on launch monitor and accessory cards.
   - Mobile bar gains Back and Summary buttons.

   Optional JSON blocks the section may provide (all have defaults):
     #sb-fit-config     fit rule numbers (clearances, tee distance...)
     #sb-lm-fit-data    per launch-monitor placement + software data,
                        keyed by product handle
     #sb-rail-config    included / not included lines, copy strings

   IIFE: no globals, no arrow functions, broad browser compat.
   ═══════════════════════════════════════════════════════════════════ */
(function() {
  'use strict';

  var LOG = '[SB]';
  var VERSION = '3.2';

  console.log(LOG, '════════════════════════════════════════════════');
  console.log(LOG, 'SIMSPACE BUILDER v' + VERSION + ' STARTING');
  console.log(LOG, '════════════════════════════════════════════════');

  /* ─────────────────────────────────────────────
     UTILITIES
     ───────────────────────────────────────────── */
  function formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function formatMoneyShort(cents) {
    /* £1,199 style (no pence) for compact lines */
    return '£' + Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function loadJsonBlock(id, fallback) {
    var el = document.getElementById(id);
    if (!el) return fallback === undefined ? [] : fallback;
    try {
      return JSON.parse(el.textContent);
    } catch(e) {
      console.error(LOG, 'Failed to parse', id, e);
      return fallback === undefined ? [] : fallback;
    }
  }

  function qs(sel, parent) {
    return (parent || document).querySelector(sel);
  }

  function qsa(sel, parent) {
    return (parent || document).querySelectorAll(sel);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str == null ? '' : String(str)));
    return div.innerHTML;
  }

  function extend(target, src) {
    if (!src || typeof src !== 'object') return target;
    var keys = Object.keys(src);
    for (var i = 0; i < keys.length; i++) target[keys[i]] = src[keys[i]];
    return target;
  }

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  var FT_PER_M = 3.28084;

  function mToUnit(m, unit) {
    if (m == null || isNaN(m)) return '';
    return unit === 'ft' ? String(round1(m * FT_PER_M)) : String(round2(m));
  }

  function unitToM(val, unit) {
    var n = parseFloat(val);
    if (isNaN(n) || n <= 0) return null;
    return unit === 'ft' ? round2(n / FT_PER_M) : round2(n);
  }

  function fmtM(m) {
    if (m == null) return '';
    return (Math.round(m * 100) / 100).toString().replace(/\.0+$/, '') + ' m';
  }

  /* ─────────────────────────────────────────────
     DOM REFS
     ───────────────────────────────────────────── */
  var root = document.getElementById('sb-root');
  if (!root) {
    console.error(LOG, 'Root element #sb-root not found, aborting');
    return;
  }

  var heroImage = qs('#sb-hero-image');
  var defaultHeroSrc = heroImage ? heroImage.getAttribute('src') : null;
  var lastAccessoryImage = null;
  var headerStep = qs('#sb-header-step');
  var headerTotal = qs('#sb-header-total');
  var mobilePrice = qs('#sb-mobile-price');
  var mobileNextBtn = qs('#sb-mobile-next');
  var mobileBar = qs('#sb-mobile-bar');
  var summaryItems = qs('#sb-summary-items');
  var summaryTotal = qs('#sb-summary-total');
  var atcBtn = qs('#sb-add-to-cart');
  var atcText = qs('#sb-atc-text');
  var lmQtyWrap = qs('#sb-lm-qty');
  var selectionsBar = qs('#sb-selections');
  var selectionsInner = qs('#sb-selections-inner');
  var layoutLeft = qs('.sb-layout__left', root);
  var layoutRight = qs('.sb-layout__right', root);
  var imageWrap = qs('#sb-image-wrap');

  /* Info drawer refs */
  var infoDrawer = qs('#sb-info-drawer');
  var infoDrawerTitle = qs('#sb-info-drawer-title');
  var infoDrawerBody = qs('#sb-info-drawer-body');

  /* Savings refs */
  var headerSavings = qs('#sb-header-savings');
  var summarySavings = qs('#sb-summary-savings');
  var summarySavingsValue = qs('#sb-summary-savings-value');
  var mobileSavings = qs('#sb-mobile-savings');

  /* Discount percentage from data attribute */
  var discountPercent = parseInt(root.getAttribute('data-discount'), 10) || 0;

  /* Rail refs (created in initRail) */
  var rail = null;
  var railBody = null;

  /* ─────────────────────────────────────────────
     LOAD VARIANT DATA
     ───────────────────────────────────────────── */
  var productVariants = loadJsonBlock('sb-product-variants');
  var platinumVariants = loadJsonBlock('sb-platinum-variants');
  var eliteVariants = loadJsonBlock('sb-elite-variants');
  var deflectionVariants = loadJsonBlock('sb-deflection-variants');

  /* Info content map */
  var infoContent = loadJsonBlock('sb-info-content', {});

  console.log(LOG, 'Product variants:', productVariants.length);
  console.log(LOG, 'Platinum variants:', platinumVariants.length);
  console.log(LOG, 'Elite variants:', eliteVariants.length);
  console.log(LOG, 'Deflection variants:', deflectionVariants.length);

  /* ─────────────────────────────────────────────
     FIT CONFIG (metres). Override via #sb-fit-config
     ───────────────────────────────────────────── */
  var fitConfig = extend({
    widthClearance: 0.20,     /* spare width wanted either side in total: "leave ~20 cm" rule */
    ceilingClearance: 0.05,   /* spare height wanted above the frame */
    swingHeightMin: 2.70,     /* below this we warn about full swings (2.7-3 m typical) */
    teeToScreen: 2.50,        /* distance from the screen face to the ball */
    golferRoom: 0.90,         /* room behind the golfer for the backswing */
    tightDepth: 0.50,         /* how far short of the ideal depth still counts as "tight" */
    lmMinCeilingOverhead: 2.75 /* default mount height for ceiling-mounted units */
  }, loadJsonBlock('sb-fit-config', {}));

  /* ─────────────────────────────────────────────
     LAUNCH MONITOR PLACEMENT + SOFTWARE DATA
     Keyed by product handle. Override / extend via #sb-lm-fit-data.
       tech        : label shown on the card
       placement   : 'side' | 'behind' | 'overhead' | null (unknown: no fit badge)
       behind      : metres needed behind the ball (placement 'behind')
       minCeiling  : metres (placement 'overhead')
       software    : one-line note shown in the summary
       softwareYr  : estimated annual software cost in pence (null = not shown)
       softwareName: name used with softwareYr
     ───────────────────────────────────────────── */
  var GSPRO_YR = 19900;
  var lmFitData = extend({
    'flightscope-mevo-gen2-launch-monitor': { tech: 'Radar', placement: 'behind', behind: 2.4, software: 'FS Golf app and 8 E6 Connect courses included, no subscription. GSPro optional for more courses.', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'square-golf-launch-monitor': { tech: 'Camera', placement: 'side', software: 'Square Golf app included. GSPro compatible.', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'gc3-launch-monitor': { tech: 'Camera', placement: 'side', software: 'Foresight Premier with a 30-day Foresight+ trial; courses from $494 a year. GSPro needs Foresight+ Gold.', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'garmin-approach-r50-launch-monitor-simulator': { tech: 'Camera', placement: 'behind', behind: 2.1, software: 'Home Tee Hero built in; course play needs a Garmin Golf membership (£89.99 a year). GSPro optional on a PC.', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'nvisage-neo-e-golf-launch-monitor': { tech: 'Camera', placement: null, software: 'Check software options with us.', softwareYr: null },
    'trugolf-launchbox': { tech: 'Camera', placement: 'side', software: 'E6 CONNECT Home with 30 courses included, no annual subscription.', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'falcon-launch-monitor': { tech: 'Camera, ceiling mounted', placement: 'overhead', minCeiling: 2.75, software: 'Foresight Premier with a 30-day Foresight+ trial; courses from $494 a year.', softwareYr: null },
    'gc-hawk-launch-monitor': { tech: 'Camera, ceiling mounted', placement: 'overhead', minCeiling: 2.75, software: 'Foresight Premier with a 30-day Foresight+ trial; courses from $494 a year.', softwareYr: null },
    'uneekor-eye-mini-lite': { tech: 'Camera', placement: 'side', software: 'Uneekor View included. GSPro needs Uneekor Pro ($199 a year).', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'uneekor-eye-mini': { tech: 'Camera', placement: 'side', software: 'Uneekor View included; Refine and GSPro need Uneekor Pro ($199 a year).', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'full-swing-kit-launch-monitor': { tech: 'Radar', placement: 'behind', behind: 3.05, software: 'Full Swing app included. Courses: Full Swing GOLF for KIT (£399 a year), GSPro or E6 Connect.', softwareYr: GSPRO_YR, softwareName: 'GSPro' },
    'golfzon-launch-monitor': { tech: 'Radar and camera', placement: null, software: 'Vision WAVE: 3 courses on the free plan; paid plans up to 150 courses.', softwareYr: null },
    'quadmax-launch-monitor': { tech: 'Camera', placement: 'side', software: 'Foresight Premier with a 30-day Foresight+ trial; courses from $494 a year.', softwareYr: null },
    'foresight-sports-gcquad-launch-monitor': { tech: 'Camera', placement: 'side', software: 'Foresight Premier with a 30-day Foresight+ trial; courses from $494 a year.', softwareYr: null }
  }, loadJsonBlock('sb-lm-fit-data', {}));

  /* ─────────────────────────────────────────────
     RAIL / COPY CONFIG. Override via #sb-rail-config
     ───────────────────────────────────────────── */
  var railConfig = extend({
    title: 'Your build',
    included: [
      'Standard impact screen',
      'Frame, bungees, velcro and foam padding',
      'Self-assembly kit with tools'
    ],
    notIncluded: [
      'Assembly service',
      'Gaming PC',
      'Simulator software subscription'
    ],
    softwareLabel: 'Simulator software',
    softwareSuffix: '/yr est., not in total',
    trustLine: 'Prices include VAT.',
    commercialUrl: '/pages/clubs-and-facilities',
    whatsappUrl: 'https://wa.me/447717305356'
  }, loadJsonBlock('sb-rail-config', {}));

  /* ─────────────────────────────────────────────
     PROJECTOR MOUNT OPTIONS — floor case vs ceiling mount, paired with a
     chosen bare projector. Override via #sb-projector-mounts JSON block.
     Prices are in pence, matching variant.price. ───────────────── */
  var projectorMounts = loadJsonBlock('sb-projector-mounts', {
    floor:   { variantId: null, price: 0, title: 'Floor projector case' },
    ceiling: { variantId: null, price: 0, title: 'Ceiling projector mount' }
  });
  function currentMount() {
    if (!state.projectorVariantId) return null;
    var m = projectorMounts[state.projectorMount] || projectorMounts.floor;
    return (m && m.variantId) ? m : null;
  }

  /* ─────────────────────────────────────────────
     SIZE-TO-SCREEN MAPPING
     ───────────────────────────────────────────── */
  var sizeToScreenMap = {
    'SIM 1': '2.3 x 2.575 m',
    'SIM 2': '2.7 x 3 m',
    'SIM 3': '3.3 x 2.525 m',
    'SIM 4': '3.6 x 2.7 m',
    'SIM 5': '3 x 3 m'
  };

  function findVariantBySize(variants, screenSize, simLabel) {
    if (variants.length === 0) return null;
    var norm = (screenSize || '').replace(/\s+/g, '').toLowerCase();
    if (norm) {
      for (var i = 0; i < variants.length; i++) {
        var opt = (variants[i].option1 || '').replace(/\s+/g, '').toLowerCase();
        if (opt === norm) return variants[i];
      }
      for (var j = 0; j < variants.length; j++) {
        var opt2 = (variants[j].option1 || '').replace(/\s+/g, '').toLowerCase();
        if (opt2.indexOf(norm) >= 0 || norm.indexOf(opt2) >= 0) return variants[j];
      }
    }
    /* Products whose variants are keyed by SIM size (e.g. UltraMAX screens:
       "SIM 3 - (...)") match on the enclosure label instead of screen dims. */
    if (simLabel) {
      var lbl = simLabel.replace(/\s+/g, '').toLowerCase();
      for (var k = 0; k < variants.length; k++) {
        var opt3 = (variants[k].option1 || '').replace(/\s+/g, '').toLowerCase();
        if (opt3.indexOf(lbl + '-') === 0 || opt3 === lbl || opt3.indexOf(lbl + '(') === 0) return variants[k];
      }
    }
    return null;
  }

  /* ─────────────────────────────────────────────
     STEP NUMBERS
     The Liquid section ships steps 1-7 (enclosure..summary).
     v3 inserts "Your room" as step 1 and shifts the rest.
     ───────────────────────────────────────────── */
  var STEP = {
    room: 1,
    enclosure: 2,
    screen: 3,
    mat: 4,
    projector: 5,
    lm: 6,
    accessories: 7,
    summary: 8
  };

  /* ─────────────────────────────────────────────
     STATE
     ───────────────────────────────────────────── */
  var state = {
    currentStep: 1,
    totalSteps: 8,
    maxStepReached: 1,

    /* Step 1: Room */
    roomType: null,        /* 'garage' | 'spare-room' | 'outbuilding' | 'commercial' */
    timeline: null,        /* purchase-intent captured on the summary step (lead scoring) */
    roomUnit: 'm',
    roomW: null,           /* metres */
    roomD: null,
    roomH: null,

    /* Step 2: Enclosure */
    enclosureSize: null,
    enclosureSizeValue: null,
    enclosureVariantId: null,
    enclosureBasePrice: 0,
    enclosureImage: null,
    enclosureDims: null,   /* { w, h, d } metres, parsed from the size string */

    /* Step 3: Screen */
    screenType: 'standard',
    screenVariantId: null,
    screenPrice: 0,

    /* Step 4: Mat */
    matType: 'none',
    matValue: null,
    matUpgradePrice: 0,

    /* Step 5: Projector */
    projectorVariantId: null,
    projectorPrice: 0,
    projectorTitle: null,
    projectorMount: 'floor',   /* 'floor' (case) | 'ceiling' (mount) — paired with a chosen projector */

    /* Step 6: Launch Monitor */
    launchMonitorVariantId: null,
    launchMonitorHandle: null,
    launchMonitorPrice: 0,
    launchMonitorQty: 1,
    launchMonitorTitle: null,

    /* Step 7: Accessories */
    accessories: {
      projectorCase: { active: false, variantId: null, price: 0, title: '' },
      sideNetting:   { active: false, variantId: null, price: 0, title: '' },
      deflection:    { active: false, variantId: null, price: 0, title: '' }
    }
  };

  var restoring = false;   /* true while applying a saved build: suppresses URL writes */
  /* state.touched: nothing is written to the URL or storage until the visitor
     interacts (or a saved build is restored), so a fresh visit keeps a clean URL. */
  state.touched = false;

  /* ─────────────────────────────────────────────
     STEP NAVIGATION
     ───────────────────────────────────────────── */
  function goToStep(stepNum) {
    if (stepNum < 1 || stepNum > state.totalSteps) return;

    var currentStepEl = qs('[data-sb-step="' + state.currentStep + '"]', root);
    var nextStepEl = qs('[data-sb-step="' + stepNum + '"]', root);
    if (!nextStepEl) return;

    if (currentStepEl && currentStepEl !== nextStepEl) {
      currentStepEl.classList.remove('is-visible');
      setTimeout(function() {
        currentStepEl.classList.remove('is-active');
      }, 300);
    }

    state.currentStep = stepNum;
    if (stepNum > state.maxStepReached) state.maxStepReached = stepNum;
    if (!restoring) state.touched = true;

    setTimeout(function() {
      nextStepEl.classList.add('is-active');
      requestAnimationFrame(function() {
        nextStepEl.classList.add('is-visible');
      });
    }, currentStepEl && currentStepEl !== nextStepEl ? 320 : 0);

    updateProgressBar();
    if (headerStep) headerStep.textContent = 'Step ' + stepNum + ' of ' + state.totalSteps;
    updateMobileBar();
    updateSelections();
    renderRail();
    refreshHeroImage();
    persist();

    if (!restoring) {
      var scrollTarget = root.getBoundingClientRect().top + window.pageYOffset - 70;
      window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
    }

    if (stepNum === STEP.summary) renderSummary();

    console.log(LOG, 'Step:', stepNum);
  }

  function updateProgressBar() {
    var steps = qsa('[data-sb-progress-step]', root);
    for (var i = 0; i < steps.length; i++) {
      var num = parseInt(steps[i].getAttribute('data-sb-progress-step'), 10);
      steps[i].classList.remove('is-current', 'is-completed');
      if (num === state.currentStep) {
        steps[i].classList.add('is-current');
      } else if (num < state.currentStep) {
        steps[i].classList.add('is-completed');
      }
    }
  }

  function updateMobileBar() {
    if (mobileNextBtn) {
      mobileNextBtn.textContent = state.currentStep === state.totalSteps ? 'Add to Cart' : 'Continue';
    }
    var backBtn = qs('#sb-mobile-back');
    if (backBtn) backBtn.style.display = state.currentStep > 1 ? '' : 'none';
  }

  /* ─────────────────────────────────────────────
     LABELS shared by chips, rail and summary
     ───────────────────────────────────────────── */
  function roomTypeLabel(type) {
    var map = { 'garage': 'Garage', 'spare-room': 'Spare room', 'outbuilding': 'Garden room or outbuilding', 'commercial': 'Commercial space' };
    return map[type] || '';
  }

  function roomDimsLabel() {
    if (state.roomW == null || state.roomD == null || state.roomH == null) return '';
    if (state.roomUnit === 'ft') {
      return mToUnit(state.roomW, 'ft') + ' × ' + mToUnit(state.roomD, 'ft') + ' × ' + mToUnit(state.roomH, 'ft') + ' ft';
    }
    return mToUnit(state.roomW, 'm') + ' × ' + mToUnit(state.roomD, 'm') + ' × ' + mToUnit(state.roomH, 'm') + ' m';
  }

  function roomLabel() {
    var parts = [];
    if (state.roomType) parts.push(roomTypeLabel(state.roomType));
    var dims = roomDimsLabel();
    if (dims) parts.push(dims);
    return parts.join(' · ');
  }

  function screenLabel() {
    if (state.screenType === 'platinum') return 'Platinum 4K';
    if (state.screenType === 'elite') return 'UltraMAX High-Contrast';
    return 'Standard';
  }

  function matLabel() {
    var label = 'No mat';
    if (state.matType === 'standard') label = 'Standard Mat';
    if (state.matType === 'tee-turf') label = 'Tee Turf Mat';
    if (state.matValue && state.matType !== 'none') label = state.matValue.split(' - ')[0] || label;
    return label;
  }

  function lmLabel() {
    var label = state.launchMonitorTitle || 'Skipped';
    if (!state.launchMonitorVariantId) label = 'Skipped';
    if (state.launchMonitorQty > 1) label += ' ×' + state.launchMonitorQty;
    return label;
  }

  function activeAccessoryNames() {
    var names = [];
    var keys = Object.keys(state.accessories);
    for (var i = 0; i < keys.length; i++) {
      if (state.accessories[keys[i]].active) names.push(state.accessories[keys[i]].title);
    }
    return names;
  }

  /* ─────────────────────────────────────────────
     SELECTION CHIPS (above the current step)
     ───────────────────────────────────────────── */
  function updateSelections() {
    if (!selectionsBar || !selectionsInner) return;

    if (state.currentStep <= 1) {
      selectionsBar.style.display = 'none';
      return;
    }

    var chips = [];

    if (state.currentStep > STEP.room) {
      chips.push(selChip('Room', roomLabel() || 'Not set', STEP.room));
    }
    if (state.currentStep > STEP.enclosure && state.enclosureSize) {
      chips.push(selChip('Enclosure', state.enclosureSize, STEP.enclosure));
    }
    if (state.currentStep > STEP.screen) {
      chips.push(selChip('Screen', screenLabel(), STEP.screen));
    }
    if (state.currentStep > STEP.mat) {
      chips.push(selChip('Mat', matLabel(), STEP.mat));
    }
    if (state.currentStep > STEP.projector) {
      chips.push(selChip('Projector', state.projectorVariantId ? (state.projectorTitle || 'Projector') : 'Skipped', STEP.projector));
    }
    if (state.currentStep > STEP.lm) {
      chips.push(selChip('Launch Monitor', lmLabel(), STEP.lm));
    }
    if (state.currentStep > STEP.accessories) {
      var accNames = activeAccessoryNames();
      chips.push(selChip('Accessories', accNames.length > 0 ? accNames.join(', ') : 'None', STEP.accessories));
    }

    if (chips.length > 0) {
      selectionsBar.style.display = '';
      selectionsInner.innerHTML = chips.join('');
    } else {
      selectionsBar.style.display = 'none';
    }
  }

  function selChip(label, value, step) {
    return '<button type="button" class="sb-sel-chip" data-sb-edit-step="' + step + '">' +
      '<span class="sb-sel-chip__label">' + escapeHtml(label) + ':</span> ' +
      '<span class="sb-sel-chip__value">' + escapeHtml(value) + '</span> ' +
      '<span class="sb-sel-chip__edit">Edit</span>' +
    '</button>';
  }

  /* ─────────────────────────────────────────────
     VARIANT RESOLUTION
     ───────────────────────────────────────────── */
  function resolveEnclosureVariant() {
    if (!state.enclosureSizeValue) return;

    var sizeVal = state.enclosureSizeValue;
    var matVal = state.matValue;

    for (var i = 0; i < productVariants.length; i++) {
      var v = productVariants[i];
      if (v.option1 !== sizeVal) continue;
      if (matVal !== null && matVal !== undefined) {
        if (v.option2 === matVal) {
          state.enclosureVariantId = v.id;
          state.enclosureBasePrice = v.price;
          console.log(LOG, 'Resolved variant:', v.id, formatMoney(v.price), '(', sizeVal, '+', matVal, ')');
          return;
        }
      } else {
        state.enclosureVariantId = v.id;
        state.enclosureBasePrice = v.price;
        console.log(LOG, 'Resolved variant:', v.id, formatMoney(v.price), '(', sizeVal, ')');
        return;
      }
    }

    for (var j = 0; j < productVariants.length; j++) {
      if (productVariants[j].option1 === sizeVal) {
        state.enclosureVariantId = productVariants[j].id;
        state.enclosureBasePrice = productVariants[j].price;
        console.log(LOG, 'Resolved variant (fallback):', productVariants[j].id, formatMoney(productVariants[j].price));
        return;
      }
    }

    console.warn(LOG, 'Could not resolve variant for', sizeVal, matVal);
  }

  function updateScreenPricesForSize() {
    var simLabel = state.enclosureSize;
    if (!simLabel) return;

    var screenSize = sizeToScreenMap[simLabel] || '';

    if (platinumVariants.length > 0) {
      var pv = findVariantBySize(platinumVariants, screenSize);
      if (pv) {
        var platCard = qs('[data-screen-type="platinum"]', root);
        if (platCard) {
          platCard.setAttribute('data-variant-id', pv.id);
          platCard.setAttribute('data-price', pv.price);
          var priceEl = qs('[data-sb-platinum-price]', platCard);
          if (priceEl) priceEl.textContent = '+' + formatMoney(pv.price);
        }
        if (state.screenType === 'platinum') {
          state.screenVariantId = pv.id;
          state.screenPrice = pv.price;
        }
      }
    }

    if (eliteVariants.length > 0) {
      var ev = findVariantBySize(eliteVariants, screenSize, simLabel);
      if (ev) {
        var eliteCard = qs('[data-screen-type="elite"]', root);
        if (eliteCard) {
          eliteCard.setAttribute('data-variant-id', ev.id);
          eliteCard.setAttribute('data-price', ev.price);
          var ePriceEl = qs('[data-sb-elite-price]', eliteCard);
          if (ePriceEl) ePriceEl.textContent = '+' + formatMoney(ev.price);
        }
        if (state.screenType === 'elite') {
          state.screenVariantId = ev.id;
          state.screenPrice = ev.price;
        }
      }
    }

    if (deflectionVariants.length > 0) {
      var dv = findVariantBySize(deflectionVariants, screenSize);
      if (dv) {
        var defCards = qsa('[data-accessory-key="deflection"]', root);
        for (var d = 0; d < defCards.length; d++) {
          defCards[d].setAttribute('data-variant-id', dv.id);
          defCards[d].setAttribute('data-price', dv.price);
          var dpEl = qs('[data-sb-deflection-price]', defCards[d]);
          if (dpEl) dpEl.textContent = '+' + formatMoney(dv.price);
        }
        if (state.accessories.deflection.active) {
          state.accessories.deflection.variantId = dv.id;
          state.accessories.deflection.price = dv.price;
        }
      }
    }
  }

  function updateMatPricesForSize() {
    if (!state.enclosureSizeValue) return;

    var matCards = qsa('[data-sb-mat-card]', root);
    var basePrice = 0;
    for (var i = 0; i < productVariants.length; i++) {
      if (productVariants[i].option1 === state.enclosureSizeValue) {
        basePrice = productVariants[i].price;
        break;
      }
    }

    state.matUpgradePrice = 0;

    for (var c = 0; c < matCards.length; c++) {
      var card = matCards[c];
      var matValue = card.getAttribute('data-mat-value');
      if (!matValue) continue;

      var matVariantPrice = basePrice;
      for (var v = 0; v < productVariants.length; v++) {
        if (productVariants[v].option1 === state.enclosureSizeValue && productVariants[v].option2 === matValue) {
          matVariantPrice = productVariants[v].price;
          break;
        }
      }

      var diff = matVariantPrice - basePrice;
      var matType = card.getAttribute('data-mat-type');
      if (matType === state.matType) state.matUpgradePrice = diff > 0 ? diff : 0;

      var priceEl = qs('[data-sb-mat-price="' + matType + '"]', card);
      if (priceEl) {
        if (diff > 0) {
          priceEl.textContent = '+' + formatMoney(diff);
          priceEl.classList.add('sb-card__price-text--upgrade');
          priceEl.classList.remove('sb-card__price-text--included');
        } else {
          priceEl.textContent = 'Included';
          priceEl.classList.remove('sb-card__price-text--upgrade');
          priceEl.classList.add('sb-card__price-text--included');
        }
      }
    }
  }

  /* ─────────────────────────────────────────────
     CALCULATE TOTAL
     ───────────────────────────────────────────── */
  function calculateTotal() {
    var total = state.enclosureBasePrice;

    if (state.screenType !== 'standard' && state.screenPrice > 0) total += state.screenPrice;
    if (state.projectorVariantId && state.projectorPrice > 0) total += state.projectorPrice;
    if (currentMount()) total += currentMount().price;
    if (state.launchMonitorVariantId && state.launchMonitorPrice > 0) total += state.launchMonitorPrice * state.launchMonitorQty;

    var accKeys = Object.keys(state.accessories);
    for (var i = 0; i < accKeys.length; i++) {
      var acc = state.accessories[accKeys[i]];
      if (acc.active && acc.price > 0) total += acc.price;
    }

    var formatted = formatMoney(total);
    if (headerTotal) headerTotal.textContent = formatted;
    if (mobilePrice) mobilePrice.textContent = formatted;
    if (summaryTotal) summaryTotal.textContent = formatted;

    var savings = savingsFor(total);
    if (savings > 0) {
      var savingsFormatted = formatMoney(savings);
      if (headerSavings) { headerSavings.textContent = 'Save ' + savingsFormatted; headerSavings.style.display = ''; }
      if (summarySavings) summarySavings.style.display = '';
      if (summarySavingsValue) summarySavingsValue.textContent = '-' + savingsFormatted;
      if (mobileSavings) { mobileSavings.textContent = 'Save ' + savingsFormatted; mobileSavings.style.display = ''; }
    } else {
      if (headerSavings) headerSavings.style.display = 'none';
      if (summarySavings) summarySavings.style.display = 'none';
      if (mobileSavings) mobileSavings.style.display = 'none';
    }

    try {
      document.dispatchEvent(new CustomEvent('simbuilder:total-updated', {
        detail: { total: total, formatted: formatted }
      }));
    } catch(e) { /* ignore */ }

    renderRail();
    persist();

    return total;
  }

  function savingsFor(total) {
    if (discountPercent > 0 && total > 0) {
      var rrp = Math.round(total / (1 - discountPercent / 100));
      return rrp - total;
    }
    return 0;
  }

  function calculateTotalSilent() {
    /* Total without touching the DOM; used by the rail */
    var total = state.enclosureBasePrice;
    if (state.screenType !== 'standard' && state.screenPrice > 0) total += state.screenPrice;
    if (state.projectorVariantId && state.projectorPrice > 0) total += state.projectorPrice;
    if (currentMount()) total += currentMount().price;
    if (state.launchMonitorVariantId && state.launchMonitorPrice > 0) total += state.launchMonitorPrice * state.launchMonitorQty;
    var accKeys = Object.keys(state.accessories);
    for (var i = 0; i < accKeys.length; i++) {
      var acc = state.accessories[accKeys[i]];
      if (acc.active && acc.price > 0) total += acc.price;
    }
    return total;
  }

  /* ─────────────────────────────────────────────
     HERO IMAGE UPDATE
     ───────────────────────────────────────────── */
  function updateHeroImage(url) {
    if (!heroImage || !url) return;
    if (heroImage.getAttribute('src') === url) return;
    heroImage.classList.add('is-fading');
    setTimeout(function() {
      heroImage.src = url;
      heroImage.classList.remove('is-fading');
    }, 200);
  }

  /* The card thumbnails are served at width=200; ask the CDN for a hero-sized copy. */
  function upscaleCardImage(url) {
    if (!url) return url;
    return url.replace(/([?&]width=)200(?=&|$)/, '$1800').replace(/_200x(200)?\./, '_800x.');
  }

  function selectedCardImage(containerAttr) {
    var wrap = qs('[' + containerAttr + ']', root);
    if (!wrap) return null;
    var card = qs('.sb-card.is-selected', wrap);
    if (!card) return null;
    /* "None" / "Skip" cards carry a placeholder icon, not a product photo */
    var kind = (card.getAttribute('data-mat-type') || card.getAttribute('data-handle') || '').toLowerCase();
    if (kind === 'none' || kind === 'skip') return null;
    var img = qs('img', card);
    return img ? upscaleCardImage(img.getAttribute('src')) : null;
  }

  /* The hero shows what the visitor is choosing right now: the current step's
     selection where one has an image (screen / mat / projector / launch monitor /
     accessories), otherwise the chosen enclosure, otherwise the section default.
     Skip and no-image cards fall through to the enclosure. */
  function heroImageForStep() {
    var url = null;
    switch (state.currentStep) {
      case STEP.screen:      url = selectedCardImage('data-sb-screen-cards'); break;
      case STEP.mat:         url = selectedCardImage('data-sb-mat-cards'); break;
      case STEP.projector:   url = selectedCardImage('data-sb-projector-cards'); break;
      case STEP.lm:          url = selectedCardImage('data-sb-lm-cards'); break;
      case STEP.accessories: url = lastAccessoryImage || selectedCardImage('data-sb-accessory-cards'); break;
    }
    return url || state.enclosureImage || defaultHeroSrc;
  }

  function refreshHeroImage() {
    var url = heroImageForStep();
    if (!url) return;
    updateHeroImage(url);
    var stepEl = qs('[data-sb-step="' + state.currentStep + '"]', root);
    if (stepEl) {
      var mImg = qs('.sb-step__mobile-image img', stepEl);
      if (mImg && mImg.getAttribute('src') !== url) mImg.src = url;
    }
  }

  /* ═════════════════════════════════════════════
     ROOM STEP (new in v3)
     ═════════════════════════════════════════════ */
  function parseEnclosureDims(sizeValue) {
    /* "SIM 1 - (8'6 x 8'2 x 4'11 ft / 2.6 x 2.5 x 1.5m)" -> { w: 2.6, h: 2.5, d: 1.5 } */
    if (!sizeValue) return null;
    var m = sizeValue.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m/i);
    if (!m) return null;
    return { w: parseFloat(m[1]), h: parseFloat(m[2]), d: parseFloat(m[3]) };
  }

  function roomIsSet() {
    return state.roomW != null && state.roomD != null && state.roomH != null;
  }

  function lmData() {
    return state.launchMonitorHandle ? (lmFitData[state.launchMonitorHandle] || null) : null;
  }

  /* Depth the whole setup needs, given an enclosure and an optional launch monitor */
  function requiredDepth(enc, lm) {
    var behind = fitConfig.golferRoom;
    if (lm && lm.placement === 'behind' && lm.behind > behind) behind = lm.behind;
    return round2(enc.d + fitConfig.teeToScreen + behind);
  }

  /* Returns { status: 'fit'|'tight'|'no'|'unknown', text, detail } */
  function fitForEnclosure(enc, lm) {
    if (!roomIsSet() || !enc) return { status: 'unknown', text: 'Add your room size to check fit' };

    var problems = [];
    var tight = [];

    /* Width */
    var spareW = round2(state.roomW - enc.w);
    if (spareW < 0) problems.push('too wide for your room by ' + Math.abs(Math.round(spareW * 100)) + ' cm');
    else if (spareW < fitConfig.widthClearance) tight.push('only ' + Math.round(spareW * 100) + ' cm spare width');

    /* Ceiling */
    var spareH = round2(state.roomH - enc.h);
    if (spareH < 0) problems.push('taller than your ceiling by ' + Math.abs(Math.round(spareH * 100)) + ' cm');
    else if (spareH < fitConfig.ceilingClearance) tight.push('only ' + Math.round(spareH * 100) + ' cm below the ceiling');

    /* Depth */
    var need = requiredDepth(enc, lm);
    var spareD = round2(state.roomD - need);
    if (spareD < -fitConfig.tightDepth) problems.push('needs about ' + fmtM(need) + ' of room length, you have ' + fmtM(state.roomD));
    else if (spareD < 0) tight.push('about ' + fmtM(need) + ' of length is ideal, you have ' + fmtM(state.roomD));

    if (problems.length) return { status: 'no', text: 'Won’t fit', detail: capitalise(problems.join('; ')) };
    if (tight.length) return { status: 'tight', text: 'Tight fit', detail: capitalise(tight.join('; ')) };

    var detail = Math.round(spareW * 100) + ' cm spare width, ' + Math.round(spareH * 100) + ' cm below the ceiling';
    return { status: 'fit', text: 'Fits your room', detail: detail };
  }

  function fitForLaunchMonitor(handle, enc) {
    var lm = lmFitData[handle];
    if (!lm || !lm.placement) return null;
    if (!roomIsSet()) return { status: 'unknown', text: lmPlacementText(lm) };

    if (lm.placement === 'overhead') {
      var minC = lm.minCeiling || fitConfig.lmMinCeilingOverhead;
      if (state.roomH < minC) return { status: 'no', text: 'Needs a ' + fmtM(minC) + '+ ceiling, yours is ' + fmtM(state.roomH) };
      return { status: 'fit', text: 'Ceiling mount OK at ' + fmtM(state.roomH) };
    }

    var encDims = enc || { d: 1.5 };
    var need = requiredDepth(encDims, lm);
    var spare = round2(state.roomD - need);
    if (spare < -fitConfig.tightDepth) return { status: 'no', text: 'Needs about ' + fmtM(need) + ' of room length, you have ' + fmtM(state.roomD) };
    if (spare < 0) return { status: 'tight', text: 'Ideally ' + fmtM(need) + ' of length, you have ' + fmtM(state.roomD) };
    return { status: 'fit', text: lmPlacementText(lm) + ' · fits in ' + fmtM(state.roomD) };
  }

  function lmPlacementText(lm) {
    if (lm.placement === 'side') return 'Sits beside the ball';
    if (lm.placement === 'behind') return 'Sits ' + fmtM(lm.behind) + ' behind the ball';
    if (lm.placement === 'overhead') return 'Ceiling mounted, ' + fmtM(lm.minCeiling || fitConfig.lmMinCeilingOverhead) + '+ ceiling';
    return '';
  }

  function capitalise(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }

  function badgeHtml(fit, extraClass) {
    if (!fit) return '';
    return '<span class="sb-fit-badge sb-fit-badge--' + fit.status + (extraClass ? ' ' + extraClass : '') + '">' +
      '<span class="sb-fit-badge__dot"></span>' + escapeHtml(fit.text) + '</span>' +
      (fit.detail ? '<span class="sb-fit-detail">' + escapeHtml(fit.detail) + '</span>' : '');
  }

  function setBadge(card, html) {
    var slot = qs('[data-sb-badge-slot]', card);
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'sb-card__badges';
      slot.setAttribute('data-sb-badge-slot', '');
      var content = qs('.sb-card__content', card);
      if (content) content.appendChild(slot); else card.appendChild(slot);
    }
    slot.innerHTML = html;
    slot.style.display = html ? '' : 'none';
  }

  function refreshFitBadges() {
    /* Enclosures */
    var encCards = qsa('[data-sb-enclosure-card]', root);
    var lm = lmData();
    var bestFit = null;
    var bestArea = 0;

    for (var i = 0; i < encCards.length; i++) {
      var card = encCards[i];
      var dims = parseEnclosureDims(card.getAttribute('data-size-value'));
      var fit = fitForEnclosure(dims, lm);
      card.classList.remove('sb-card--fit-no', 'sb-card--fit-tight', 'sb-card--fit-fit');
      if (fit.status !== 'unknown') card.classList.add('sb-card--fit-' + fit.status);
      card.setAttribute('data-sb-fit', fit.status);
      setBadge(card, badgeHtml(fit));
      if (fit.status === 'fit' && dims && dims.w * dims.h > bestArea) {
        bestArea = dims.w * dims.h;
        bestFit = card;
      }
    }
    for (var b = 0; b < encCards.length; b++) {
      var star = qs('.sb-best-fit', encCards[b]);
      if (encCards[b] === bestFit) {
        if (!star) {
          star = document.createElement('span');
          star.className = 'sb-best-fit';
          star.textContent = 'Best fit';
          encCards[b].appendChild(star);
        }
      } else if (star) {
        star.parentNode.removeChild(star);
      }
    }

    /* Launch monitors */
    var lmCards = qsa('[data-sb-lm-card]', root);
    for (var j = 0; j < lmCards.length; j++) {
      var handle = lmCards[j].getAttribute('data-handle');
      if (!handle || handle === 'skip') continue;
      var data = lmFitData[handle];
      var html = '';
      if (data && data.tech) html += '<span class="sb-tech-tag">' + escapeHtml(data.tech) + '</span>';
      var lmFit = fitForLaunchMonitor(handle, state.enclosureDims);
      if (lmFit) html += badgeHtml(lmFit);
      lmCards[j].classList.remove('sb-card--fit-no', 'sb-card--fit-tight', 'sb-card--fit-fit');
      if (lmFit && lmFit.status !== 'unknown') lmCards[j].classList.add('sb-card--fit-' + lmFit.status);
      setBadge(lmCards[j], html);
    }

    /* Accessories: side netting is 2.5 m high, SIM 5 is 3 m */
    var nettingCards = qsa('[data-accessory-key="sideNetting"]', root);
    for (var n = 0; n < nettingCards.length; n++) {
      var encH = state.enclosureDims ? state.enclosureDims.h : null;
      var nettingFit;
      if (encH && encH > 2.5) {
        nettingFit = { status: 'tight', text: 'Made for 2.5 m enclosures, ' + (state.enclosureSize || 'this size') + ' is ' + fmtM(encH) + ' high' };
      } else {
        nettingFit = { status: 'fit', text: 'Fits ' + (state.enclosureSize || 'this enclosure') };
      }
      setBadge(nettingCards[n], badgeHtml(nettingFit));
    }

    /* Room step hint */
    var hint = qs('#sb-room-fit-hint');
    if (hint) {
      if (!roomIsSet()) {
        hint.textContent = 'Add your room size and we’ll mark which enclosures and launch monitors fit. Not sure yet? You can skip this and come back.';
        hint.className = 'sb-room-dims__hint';
      } else {
        var fits = 0;
        for (var k = 0; k < encCards.length; k++) if (encCards[k].getAttribute('data-sb-fit') === 'fit') fits++;
        var lowCeiling = state.roomH < fitConfig.swingHeightMin;
        var msg;
        if (fits === 0) {
          msg = 'None of our enclosures fit comfortably in ' + roomDimsLabel() + '. Message us on WhatsApp and we’ll look at options.';
        } else {
          msg = fits + ' of ' + encCards.length + ' enclosure sizes fit ' + roomDimsLabel() + '.';
        }
        if (lowCeiling) msg += ' Your ceiling is under ' + fmtM(fitConfig.swingHeightMin) + ', so check your full swing height with a club before ordering.';
        hint.textContent = msg;
        hint.className = 'sb-room-dims__hint ' + (fits === 0 ? 'sb-room-dims__hint--warn' : 'sb-room-dims__hint--ok');
      }
    }
  }

  function buildRoomStepMarkup() {
    var wa = railConfig.whatsappUrl + '?text=' + encodeURIComponent('Hi, can you check my room size for a SimSpace enclosure? Length x width x height: ');
    return '' +
      '<div class="sb-step__header">' +
        '<h2 class="sb-step__title">Your room.</h2>' +
        '<p class="sb-step__subtitle">Tell us where the simulator is going and we’ll check what fits.</p>' +
      '</div>' +
      '<div class="sb-cards sb-cards--grid" data-sb-room-type-cards>' +
        roomTypeCard('garage', 'Garage', 'Single or double garage at home.') +
        roomTypeCard('spare-room', 'Spare room', 'Bedroom, loft, basement or garage conversion.') +
        roomTypeCard('outbuilding', 'Garden room or outbuilding', 'Purpose-built space or barn.') +
        roomTypeCard('commercial', 'Commercial space', 'Bar, club, coaching studio or office.') +
      '</div>' +
      '<div class="sb-commercial-note" id="sb-commercial-note" style="display:none;">' +
        '<strong>Fitting out a venue?</strong> Our commercial team specs bays, flooring and software for clubs and businesses. ' +
        '<a href="' + escapeHtml(railConfig.commercialUrl) + '">Talk to the clubs and facilities team</a> or carry on and build a spec here.' +
      '</div>' +
      '<div class="sb-room-dims" id="sb-room-dims">' +
        '<div class="sb-room-dims__head">' +
          '<span class="sb-room-dims__title">Room size</span>' +
          '<div class="sb-unit-toggle" role="group" aria-label="Unit of measure">' +
            '<button type="button" class="sb-unit-toggle__btn is-active" data-sb-unit="m">m</button>' +
            '<button type="button" class="sb-unit-toggle__btn" data-sb-unit="ft">ft</button>' +
          '</div>' +
        '</div>' +
        '<p class="sb-room-dims__min">Most home sims need at least <strong>3.0 m wide × 2.7 m high × 4.2 m long</strong>. A typical 2.4 m garage ceiling is usually too low — we’ll help you find the best fit.</p>' +
        '<div class="sb-room-dims__fields">' +
          dimField('w', 'Width', 'Wall to wall, across the screen') +
          dimField('d', 'Length', 'Screen wall to the wall behind you') +
          dimField('h', 'Ceiling height', 'Lowest point over the hitting area') +
        '</div>' +
        '<button type="button" class="sb-room-dims__typical" data-sb-typical>I don’t know my dimensions — use a recommended size</button>' +
        '<p class="sb-room-dims__typical-note" data-sb-typical-note style="display:none;"></p>' +
        '<p class="sb-room-dims__hint" id="sb-room-fit-hint"></p>' +
      '</div>' +
      '<div class="sb-help-link">' +
        '<button type="button" class="sb-help-toggle" data-sb-help-toggle>Not sure how to measure? ' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>' +
        '</button>' +
        '<div class="sb-help-expand" data-sb-help-expand>' +
          '<p>Measure the clear space you can use: width across the screen wall, length from that wall to the wall behind you, and the lowest ceiling point over where you’ll stand. Leave about 20 cm of width spare, and check you can swing a driver freely. Most players want a 2.7 to 3 m ceiling.</p>' +
          '<div class="sb-help-ctas"><a href="' + escapeHtml(wa) + '" target="_blank" rel="noopener" class="sb-help-cta">Check my room on WhatsApp</a></div>' +
        '</div>' +
      '</div>' +
      '<div class="sb-nav">' +
        '<div></div>' +
        '<button type="button" class="sb-nav__btn sb-nav__btn--next" data-sb-next>Continue</button>' +
      '</div>';
  }

  function roomTypeCard(type, title, desc) {
    return '<div class="sb-card sb-card--room" data-sb-room-type="' + type + '" role="button" tabindex="0">' +
      '<div class="sb-card__content">' +
        '<div class="sb-card__title">' + escapeHtml(title) + '</div>' +
        '<div class="sb-card__desc">' + escapeHtml(desc) + '</div>' +
      '</div>' +
    '</div>';
  }

  function dimField(key, label, help) {
    return '<label class="sb-dim">' +
      '<span class="sb-dim__label">' + escapeHtml(label) + '</span>' +
      '<span class="sb-dim__input-wrap">' +
        '<input type="number" inputmode="decimal" min="0" step="0.01" class="sb-dim__input" data-sb-dim="' + key + '" placeholder="0.0">' +
        '<span class="sb-dim__unit" data-sb-dim-unit>m</span>' +
      '</span>' +
      '<span class="sb-dim__help">' + escapeHtml(help) + '</span>' +
    '</label>';
  }

  function injectRoomStep() {
    if (qs('[data-sb-room-step]', root)) {
      console.log(LOG, 'Room step already in markup, skipping injection');
      return;
    }

    /* Shift existing steps 1..7 to 2..8 */
    var steps = qsa('.sb-step[data-sb-step]', root);
    var maxExisting = 0;
    for (var i = 0; i < steps.length; i++) {
      var n = parseInt(steps[i].getAttribute('data-sb-step'), 10);
      if (n > maxExisting) maxExisting = n;
    }
    /* Renumber from the highest down so ids never collide */
    for (var s = maxExisting; s >= 1; s--) {
      var stepEl = qs('.sb-step[data-sb-step="' + s + '"]', root);
      if (!stepEl) continue;
      stepEl.setAttribute('data-sb-step', String(s + 1));
      if (stepEl.id === 'sb-step-' + s) stepEl.id = 'sb-step-' + (s + 1);
      stepEl.classList.remove('is-active', 'is-visible');
    }
    state.totalSteps = maxExisting + 1;

    /* Progress bar: renumber and prepend a new step-1 node */
    var progressSteps = qsa('[data-sb-progress-step]', root);
    for (var p = progressSteps.length - 1; p >= 0; p--) {
      var pn = parseInt(progressSteps[p].getAttribute('data-sb-progress-step'), 10);
      progressSteps[p].setAttribute('data-sb-progress-step', String(pn + 1));
      var numEl = qs('.sb-progress__num', progressSteps[p]);
      if (numEl) numEl.textContent = String(pn + 1);
      progressSteps[p].classList.remove('is-current', 'is-completed');
    }
    if (progressSteps.length > 0) {
      var template = progressSteps[0];
      var newNode = template.cloneNode(true);
      newNode.setAttribute('data-sb-progress-step', '1');
      var newNum = qs('.sb-progress__num', newNode);
      if (newNum) newNum.textContent = '1';
      if (!qs('.sb-progress__line', newNode)) {
        var line = document.createElement('div');
        line.className = 'sb-progress__line';
        newNode.appendChild(line);
      }
      newNode.classList.add('is-current');
      template.parentNode.insertBefore(newNode, template);
    }

    /* Build and insert the room step before the enclosure step */
    var roomStep = document.createElement('div');
    roomStep.className = 'sb-step';
    roomStep.id = 'sb-step-1';
    roomStep.setAttribute('data-sb-step', '1');
    roomStep.setAttribute('data-sb-room-step', '');
    roomStep.innerHTML = buildRoomStepMarkup();

    var enclosureStep = qs('.sb-step[data-sb-step="2"]', root);
    if (enclosureStep && enclosureStep.parentNode) {
      enclosureStep.parentNode.insertBefore(roomStep, enclosureStep);
    } else if (layoutRight) {
      layoutRight.appendChild(roomStep);
    }

    if (headerStep) headerStep.textContent = 'Step 1 of ' + state.totalSteps;
    console.log(LOG, 'Room step injected; total steps:', state.totalSteps);
  }

  function initRoomStep() {
    var cards = qsa('[data-sb-room-type]', root);
    var note = qs('#sb-commercial-note');

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        function select() {
          for (var j = 0; j < cards.length; j++) cards[j].classList.remove('is-selected');
          card.classList.add('is-selected');
          state.roomType = card.getAttribute('data-sb-room-type');
          if (note) note.style.display = state.roomType === 'commercial' ? '' : 'none';
          renderRail();
          persist();
          console.log(LOG, 'Room type:', state.roomType);
        }
        card.addEventListener('click', select);
        card.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); }
        });
      })(cards[i]);
    }

    /* Unit toggle */
    var unitBtns = qsa('[data-sb-unit]', root);
    for (var u = 0; u < unitBtns.length; u++) {
      (function(btn) {
        btn.addEventListener('click', function() {
          setUnit(btn.getAttribute('data-sb-unit'));
        });
      })(unitBtns[u]);
    }

    /* Dimension inputs */
    var inputs = qsa('[data-sb-dim]', root);
    for (var d = 0; d < inputs.length; d++) {
      (function(input) {
        var handler = function() {
          var key = input.getAttribute('data-sb-dim');
          var m = unitToM(input.value, state.roomUnit);
          if (key === 'w') state.roomW = m;
          if (key === 'd') state.roomD = m;
          if (key === 'h') state.roomH = m;
          refreshFitBadges();
          renderRail();
          persist();
        };
        input.addEventListener('input', handler);
        input.addEventListener('change', handler);
      })(inputs[d]);
    }

    /* "I don't know my dimensions" — fill typical UK sizes for the chosen room type
       (borrowed from the Full Swing builder's recommended-ranges fallback). */
    var typicalBtn = qs('[data-sb-typical]', root);
    if (typicalBtn) {
      typicalBtn.addEventListener('click', function() {
        var preset = TYPICAL_ROOMS[state.roomType] || TYPICAL_ROOMS.garage;
        state.roomW = preset.w; state.roomD = preset.d; state.roomH = preset.h;
        syncRoomInputs();
        refreshFitBadges();
        renderRail();
        persist();
        var note = qs('[data-sb-typical-note]', root);
        if (note) {
          note.textContent = 'Filled with a recommended ' + preset.label + ' (' +
            fmtM(preset.w) + ' × ' + fmtM(preset.d) + ' × ' + fmtM(preset.h) +
            '). Our supplier recommends at least 3.0 m wide × 2.7 m high × 4.2 m long — enter your real space to check the fit.';
          note.style.display = '';
        }
        console.log(LOG, 'Typical room applied:', state.roomType || 'garage');
      });
    }

    refreshFitBadges();
  }

  /* Recommended workable room footprints (metres) for the dimensions fallback.
     Anchored on our supplier/GolfBays absolute minimum of 3.0 W x 2.7 H x 4.2 D —
     an average 2.4 m garage ceiling is too low, so these fill a size that FITS. */
  var TYPICAL_ROOMS = {
    'garage':      { w: 3.0, d: 5.0, h: 2.7, label: 'garage that fits a sim' },
    'spare-room':  { w: 3.0, d: 4.2, h: 2.7, label: 'spare room' },
    'outbuilding': { w: 3.5, d: 5.5, h: 2.7, label: 'garden room' },
    'commercial':  { w: 5.0, d: 7.0, h: 3.0, label: 'commercial bay' }
  };

  /* Purchase-intent options (borrowed from the Full Swing builder's Timeline step). */
  var PURCHASE_TIMELINES = [
    { v: 'ready',    label: 'Ready now (0–30 days)' },
    { v: '1-3m',     label: '1–3 months' },
    { v: '3-6m',     label: '3–6 months' },
    { v: '6m+',      label: '6+ months' },
    { v: 'research', label: 'Just researching' }
  ];
  function timelineLabel(v) {
    for (var i = 0; i < PURCHASE_TIMELINES.length; i++) {
      if (PURCHASE_TIMELINES[i].v === v) return PURCHASE_TIMELINES[i].label;
    }
    return '';
  }

  function setUnit(unit) {
    if (unit !== 'm' && unit !== 'ft') return;
    state.roomUnit = unit;
    var unitBtns = qsa('[data-sb-unit]', root);
    for (var i = 0; i < unitBtns.length; i++) {
      unitBtns[i].classList.toggle('is-active', unitBtns[i].getAttribute('data-sb-unit') === unit);
    }
    var unitLabels = qsa('[data-sb-dim-unit]', root);
    for (var l = 0; l < unitLabels.length; l++) unitLabels[l].textContent = unit;
    syncRoomInputs();
    renderRail();
    persist();
  }

  function syncRoomInputs() {
    var map = { w: state.roomW, d: state.roomD, h: state.roomH };
    var inputs = qsa('[data-sb-dim]', root);
    for (var i = 0; i < inputs.length; i++) {
      var key = inputs[i].getAttribute('data-sb-dim');
      inputs[i].value = map[key] == null ? '' : mToUnit(map[key], state.roomUnit);
    }
  }

  /* ─────────────────────────────────────────────
     STEP: ENCLOSURE CARDS
     ───────────────────────────────────────────── */
  function setEnclosureFromCard(card) {
    state.enclosureSize = card.getAttribute('data-sim-label');
    state.enclosureSizeValue = card.getAttribute('data-size-value');
    state.enclosureBasePrice = parseInt(card.getAttribute('data-price'), 10) || 0;
    state.enclosureImage = card.getAttribute('data-image') || null;
    state.enclosureDims = parseEnclosureDims(state.enclosureSizeValue);
  }

  function initEnclosureCards() {
    var cards = qsa('[data-sb-enclosure-card]', root);
    if (cards.length === 0) return;

    setEnclosureFromCard(cards[0]);

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        card.addEventListener('click', function() {
          for (var j = 0; j < cards.length; j++) cards[j].classList.remove('is-selected');
          card.classList.add('is-selected');

          setEnclosureFromCard(card);
          updateScreenPricesForSize();
          updateMatPricesForSize();
          resolveEnclosureVariant();
          refreshHeroImage();
          refreshFitBadges();
          calculateTotal();
          console.log(LOG, 'Enclosure:', state.enclosureSize, formatMoney(state.enclosureBasePrice));
        });
      })(cards[i]);
    }

    resolveEnclosureVariant();
    console.log(LOG, 'Enclosure cards:', cards.length);
  }

  /* ─────────────────────────────────────────────
     STEP: SCREEN CARDS
     ───────────────────────────────────────────── */
  function initScreenCards() {
    var cards = qsa('[data-sb-screen-card]', root);
    if (cards.length === 0) return;

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        card.addEventListener('click', function() {
          for (var j = 0; j < cards.length; j++) cards[j].classList.remove('is-selected');
          card.classList.add('is-selected');

          var screenType = card.getAttribute('data-screen-type');
          state.screenType = screenType;
          if (screenType === 'standard') {
            state.screenVariantId = null;
            state.screenPrice = 0;
          } else {
            state.screenVariantId = parseInt(card.getAttribute('data-variant-id'), 10) || null;
            state.screenPrice = parseInt(card.getAttribute('data-price'), 10) || 0;
          }
          refreshHeroImage();
          calculateTotal();
          console.log(LOG, 'Screen:', screenType, state.screenPrice ? formatMoney(state.screenPrice) : 'included');
        });
      })(cards[i]);
    }
  }

  /* ─────────────────────────────────────────────
     STEP: MAT CARDS
     ───────────────────────────────────────────── */
  function initMatCards() {
    var cards = qsa('[data-sb-mat-card]', root);
    if (cards.length === 0) return;

    var firstCard = cards[0];
    state.matType = firstCard.getAttribute('data-mat-type') || 'none';
    state.matValue = firstCard.getAttribute('data-mat-value') || null;

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        card.addEventListener('click', function() {
          for (var j = 0; j < cards.length; j++) cards[j].classList.remove('is-selected');
          card.classList.add('is-selected');

          state.matType = card.getAttribute('data-mat-type') || 'none';
          state.matValue = card.getAttribute('data-mat-value') || null;
          resolveEnclosureVariant();
          updateMatPricesForSize();
          refreshHeroImage();
          calculateTotal();
          console.log(LOG, 'Mat:', state.matType, state.matValue);
        });
      })(cards[i]);
    }

    resolveEnclosureVariant();
  }

  /* ─────────────────────────────────────────────
     STEP: PROJECTOR CARDS
     ───────────────────────────────────────────── */
  function initProjectorCards() {
    var cards = qsa('[data-sb-projector-card]', root);
    if (cards.length === 0) return;

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        card.addEventListener('click', function() {
          for (var j = 0; j < cards.length; j++) cards[j].classList.remove('is-selected');
          card.classList.add('is-selected');

          var handle = card.getAttribute('data-handle');
          if (handle === 'skip') {
            state.projectorVariantId = null;
            state.projectorPrice = 0;
            state.projectorTitle = null;
          } else {
            state.projectorVariantId = parseInt(card.getAttribute('data-variant-id'), 10) || null;
            state.projectorPrice = parseInt(card.getAttribute('data-price'), 10) || 0;
            state.projectorTitle = card.getAttribute('data-title') || '';
          }
          updateMountToggle();
          refreshHeroImage();
          calculateTotal();
          renderRail();
          persist();
          console.log(LOG, 'Projector:', handle, state.projectorPrice ? formatMoney(state.projectorPrice) : 'skip');
        });
      })(cards[i]);
    }

    /* Floor / ceiling mount toggle — only meaningful once a projector is chosen */
    var mountBtns = qsa('[data-sb-mount]', root);
    for (var mb = 0; mb < mountBtns.length; mb++) {
      (function(btn) {
        btn.addEventListener('click', function() {
          state.projectorMount = btn.getAttribute('data-sb-mount') === 'ceiling' ? 'ceiling' : 'floor';
          updateMountToggle();
          refreshHeroImage();
          calculateTotal();
          renderRail();
          persist();
          console.log(LOG, 'Projector mount:', state.projectorMount);
        });
      })(mountBtns[mb]);
    }

    updateMountToggle();
  }

  function updateMountToggle() {
    var wrap = qs('[data-sb-projector-mount]', root);
    if (wrap) wrap.style.display = state.projectorVariantId ? '' : 'none';
    var mountBtns = qsa('[data-sb-mount]', root);
    for (var i = 0; i < mountBtns.length; i++) {
      var isCeiling = mountBtns[i].getAttribute('data-sb-mount') === 'ceiling';
      mountBtns[i].classList.toggle('is-active', (isCeiling ? 'ceiling' : 'floor') === state.projectorMount);
    }
  }

  /* ─────────────────────────────────────────────
     STEP: LAUNCH MONITOR CARDS
     ───────────────────────────────────────────── */
  function initLaunchMonitorCards() {
    var cards = qsa('[data-sb-lm-card]', root);
    if (cards.length === 0) return;

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        card.addEventListener('click', function() {
          for (var j = 0; j < cards.length; j++) cards[j].classList.remove('is-selected');
          card.classList.add('is-selected');

          var handle = card.getAttribute('data-handle');
          if (handle === 'skip') {
            state.launchMonitorVariantId = null;
            state.launchMonitorHandle = null;
            state.launchMonitorPrice = 0;
            state.launchMonitorQty = 1;
            state.launchMonitorTitle = null;
            if (lmQtyWrap) lmQtyWrap.style.display = 'none';
          } else {
            state.launchMonitorVariantId = parseInt(card.getAttribute('data-variant-id'), 10) || null;
            state.launchMonitorHandle = handle;
            state.launchMonitorPrice = parseInt(card.getAttribute('data-price'), 10) || 0;
            state.launchMonitorTitle = card.getAttribute('data-title') || '';
            state.launchMonitorQty = 1;
            if (lmQtyWrap) lmQtyWrap.style.display = 'flex';
            var qtyVal = qs('[data-sb-lm-qty-val]');
            if (qtyVal) qtyVal.textContent = '1';
          }
          refreshFitBadges();
          refreshHeroImage();
          calculateTotal();
          console.log(LOG, 'Launch Monitor:', handle, state.launchMonitorPrice ? formatMoney(state.launchMonitorPrice) : 'skip');
        });
      })(cards[i]);
    }

    var minusBtn = qs('[data-sb-lm-qty-minus]');
    var plusBtn = qs('[data-sb-lm-qty-plus]');
    var qtyValEl = qs('[data-sb-lm-qty-val]');

    if (minusBtn) {
      minusBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        if (state.launchMonitorQty > 1) {
          state.launchMonitorQty--;
          if (qtyValEl) qtyValEl.textContent = state.launchMonitorQty;
          calculateTotal();
        }
      });
    }
    if (plusBtn) {
      plusBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        if (state.launchMonitorQty < 10) {
          state.launchMonitorQty++;
          if (qtyValEl) qtyValEl.textContent = state.launchMonitorQty;
          calculateTotal();
        }
      });
    }
  }

  function setLaunchMonitorQty(qty) {
    qty = Math.max(1, Math.min(10, parseInt(qty, 10) || 1));
    state.launchMonitorQty = qty;
    var qtyValEl = qs('[data-sb-lm-qty-val]');
    if (qtyValEl) qtyValEl.textContent = qty;
  }

  /* ─────────────────────────────────────────────
     STEP: ACCESSORY TOGGLE CARDS
     ───────────────────────────────────────────── */
  function initAccessoryCards() {
    var cards = qsa('[data-sb-accessory-card]', root);
    if (cards.length === 0) return;

    for (var i = 0; i < cards.length; i++) {
      (function(card) {
        var key = card.getAttribute('data-accessory-key');
        if (!key || !state.accessories[key]) return;

        state.accessories[key].variantId = parseInt(card.getAttribute('data-variant-id'), 10) || null;
        state.accessories[key].price = parseInt(card.getAttribute('data-price'), 10) || 0;
        state.accessories[key].title = card.getAttribute('data-title') || '';

        card.addEventListener('click', function() {
          var acc = state.accessories[key];
          acc.active = !acc.active;
          acc.variantId = parseInt(card.getAttribute('data-variant-id'), 10) || null;
          acc.price = parseInt(card.getAttribute('data-price'), 10) || 0;
          card.classList.toggle('is-selected', acc.active);
          if (acc.active) {
            var accImg = qs('img', card);
            lastAccessoryImage = accImg ? upscaleCardImage(accImg.getAttribute('src')) : null;
          } else {
            lastAccessoryImage = null;
          }
          refreshHeroImage();
          calculateTotal();
          console.log(LOG, 'Accessory', key, acc.active ? 'ON' : 'OFF');
        });
      })(cards[i]);
    }
  }

  /* ═════════════════════════════════════════════
     SUMMARY ROWS (shared by the rail and the final step)
     ═════════════════════════════════════════════ */
  function buildRows() {
    var rows = [];
    var reached = state.maxStepReached;

    rows.push({
      label: 'Room',
      value: roomLabel() || (reached > STEP.room ? 'Not set' : 'Not set yet'),
      price: null,
      step: STEP.room,
      muted: !roomLabel()
    });

    rows.push({
      label: 'Enclosure',
      value: 'SimSpace ' + (state.enclosureSize || ''),
      detail: state.enclosureSizeValue ? (state.enclosureSizeValue.split(' - ')[1] || '') : '',
      price: state.enclosureBasePrice,
      step: STEP.enclosure
    });

    rows.push({
      label: 'Screen',
      value: screenLabel() + ' impact screen',
      price: state.screenType !== 'standard' && state.screenPrice > 0 ? state.screenPrice : 0,
      included: state.screenType === 'standard',
      step: STEP.screen
    });

    rows.push({
      label: 'Hitting mat',
      value: matLabel(),
      price: state.matUpgradePrice > 0 ? state.matUpgradePrice : 0,
      included: state.matType !== 'none' && !(state.matUpgradePrice > 0),
      none: state.matType === 'none',
      step: STEP.mat
    });

    var projMount = currentMount();
    rows.push({
      label: 'Projector',
      value: state.projectorVariantId ? (state.projectorTitle || 'Projector') : (reached > STEP.projector ? 'Skipped' : 'Not chosen yet'),
      detail: projMount ? ((state.projectorMount === 'ceiling' ? 'Ceiling mount' : 'Floor case') + ' — ' + projMount.title) : '',
      price: state.projectorVariantId ? (state.projectorPrice + (projMount ? projMount.price : 0)) : null,
      muted: !state.projectorVariantId,
      step: STEP.projector
    });

    rows.push({
      label: 'Launch monitor',
      value: state.launchMonitorVariantId ? lmLabel() : (reached > STEP.lm ? 'Skipped' : 'Not chosen yet'),
      price: state.launchMonitorVariantId ? state.launchMonitorPrice * state.launchMonitorQty : null,
      muted: !state.launchMonitorVariantId,
      step: STEP.lm
    });

    var accNames = activeAccessoryNames();
    var accTotal = 0;
    var accKeys = Object.keys(state.accessories);
    for (var i = 0; i < accKeys.length; i++) {
      var acc = state.accessories[accKeys[i]];
      if (acc.active && acc.price > 0) accTotal += acc.price;
    }
    rows.push({
      label: 'Accessories',
      value: accNames.length ? accNames.join(', ') : (reached > STEP.accessories ? 'None' : 'Not chosen yet'),
      price: accNames.length ? accTotal : null,
      muted: !accNames.length,
      step: STEP.accessories
    });

    return rows;
  }

  function rowPriceText(row) {
    if (row.included) return 'Included';
    if (row.none) return '—';
    if (row.price == null) return '—';
    if (row.price === 0) return 'Included';
    return formatMoney(row.price);
  }

  function softwareLine() {
    var lm = lmData();
    if (!lm) return null;
    return {
      note: lm.software || '',
      yr: lm.softwareYr || null,
      name: lm.softwareName || ''
    };
  }

  /* ─────────────────────────────────────────────
     STEP: FINAL SUMMARY
     ───────────────────────────────────────────── */
  function renderSummary() {
    if (!summaryItems) return;

    /* restore captured purchase-intent across reloads */
    if (state.timeline == null) {
      try { var _tl = window.localStorage.getItem('sb_timeline_v3'); if (_tl) state.timeline = _tl; } catch(e) {}
    }

    var rows = buildRows();
    var html = '';
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var name = r.label === 'Room' ? 'Room' : r.value;
      var detail = r.label === 'Room' ? r.value : (r.detail || '');
      html += summaryRow(name, detail, rowPriceText(r), r.step, r.muted);
    }

    var sw = softwareLine();
    if (sw && (sw.yr || sw.note)) {
      html += '<div class="sb-summary__item sb-summary__item--note">' +
        '<div class="sb-summary__item-left">' +
          '<div class="sb-summary__item-name">' + escapeHtml(railConfig.softwareLabel) + '</div>' +
          '<div class="sb-summary__item-detail">' + escapeHtml(sw.note) + '</div>' +
        '</div>' +
        '<div class="sb-summary__item-right"><span class="sb-summary__item-price sb-summary__item-price--muted">' +
          (sw.yr ? 'from ' + formatMoneyShort(sw.yr) + escapeHtml(railConfig.softwareSuffix) : 'Not in total') +
        '</span></div>' +
      '</div>';
    }

    /* Purchase-intent capture (lead scoring) — borrowed from Full Swing's Timeline step */
    var tlOpts = '<option value="">Select…</option>';
    for (var t = 0; t < PURCHASE_TIMELINES.length; t++) {
      tlOpts += '<option value="' + PURCHASE_TIMELINES[t].v + '"' +
        (state.timeline === PURCHASE_TIMELINES[t].v ? ' selected' : '') + '>' +
        escapeHtml(PURCHASE_TIMELINES[t].label) + '</option>';
    }
    html += '<div class="sb-summary__timeline">' +
      '<label class="sb-summary__timeline-label" for="sb-timeline">When are you looking to buy?</label>' +
      '<select class="sb-summary__timeline-select" id="sb-timeline" data-sb-timeline>' + tlOpts + '</select>' +
      '<p class="sb-summary__timeline-help">Helps us prioritise your quote — no obligation.</p>' +
    '</div>';

    summaryItems.innerHTML = html;
    calculateTotal();
  }

  function summaryRow(name, detail, priceText, editStep, muted) {
    return '<div class="sb-summary__item' + (muted ? ' sb-summary__item--muted' : '') + '">' +
      '<div class="sb-summary__item-left">' +
        '<div class="sb-summary__item-name">' + escapeHtml(name) + '</div>' +
        (detail ? '<div class="sb-summary__item-detail">' + escapeHtml(detail) + '</div>' : '') +
      '</div>' +
      '<div class="sb-summary__item-right">' +
        '<span class="sb-summary__item-price">' + escapeHtml(priceText) + '</span>' +
        '<button type="button" class="sb-summary__item-edit" data-sb-edit-step="' + editStep + '">Edit</button>' +
      '</div>' +
    '</div>';
  }

  /* ═════════════════════════════════════════════
     "YOUR BUILD" RAIL (new in v3)
     Desktop: sticks under the hero image in the left column.
     Mobile: bottom sheet opened from the sticky bar.
     ═════════════════════════════════════════════ */
  function initRail() {
    rail = document.createElement('aside');
    rail.className = 'sb-rail';
    rail.id = 'sb-rail';
    rail.setAttribute('aria-label', railConfig.title);
    rail.innerHTML =
      '<div class="sb-rail__head">' +
        '<span class="sb-rail__title">' + escapeHtml(railConfig.title) + '</span>' +
        '<div class="sb-rail__actions">' +
          '<button type="button" class="sb-rail__share" id="sb-share-btn" title="Copy a link to this build">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>' +
            '<span>Copy link</span>' +
          '</button>' +
          '<button type="button" class="sb-rail__close" data-sb-rail-close aria-label="Close summary">' +
            '<svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="sb-rail__body" id="sb-rail-body"></div>';

    root.appendChild(rail);
    railBody = qs('#sb-rail-body', rail);

    /* Overlay for the mobile sheet */
    var overlay = document.createElement('div');
    overlay.className = 'sb-rail-overlay';
    overlay.id = 'sb-rail-overlay';
    overlay.setAttribute('data-sb-rail-close', '');
    root.appendChild(overlay);

    rail.addEventListener('click', function(e) {
      var editBtn = e.target.closest('[data-sb-edit-step]');
      if (editBtn) {
        closeRail();
        goToStep(parseInt(editBtn.getAttribute('data-sb-edit-step'), 10));
        return;
      }
      if (e.target.closest('[data-sb-rail-close]')) closeRail();
    });
    overlay.addEventListener('click', closeRail);

    var shareBtn = qs('#sb-share-btn', rail);
    if (shareBtn) {
      shareBtn.addEventListener('click', function() {
        persist();
        copyText(window.location.href, function(ok) {
          var label = qs('span', shareBtn);
          if (label) label.textContent = ok ? 'Copied' : 'Copy failed';
          setTimeout(function() { if (label) label.textContent = 'Copy link'; }, 2000);
        });
      });
    }

    placeRail();
    if (window.matchMedia) {
      var mq = window.matchMedia('(min-width: 900px)');
      var onChange = function() { placeRail(); };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
  }

  function placeRail() {
    if (!rail) return;
    var desktop = window.innerWidth >= 900;
    if (desktop && layoutLeft) {
      var stack = qs('#sb-left-stack');
      if (!stack) {
        stack = document.createElement('div');
        stack.className = 'sb-left-stack';
        stack.id = 'sb-left-stack';
        if (imageWrap && imageWrap.parentNode === layoutLeft) {
          layoutLeft.insertBefore(stack, imageWrap);
          stack.appendChild(imageWrap);
        } else {
          layoutLeft.appendChild(stack);
        }
      }
      if (rail.parentNode !== stack) stack.appendChild(rail);
      rail.classList.remove('is-open');
      rail.classList.add('sb-rail--desktop');
      rail.classList.remove('sb-rail--sheet');
    } else {
      if (rail.parentNode !== root) root.appendChild(rail);
      rail.classList.add('sb-rail--sheet');
      rail.classList.remove('sb-rail--desktop');
    }
  }

  function openRail() {
    if (!rail) return;
    renderRail();
    rail.classList.add('is-open');
    var overlay = qs('#sb-rail-overlay');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeRail() {
    if (!rail) return;
    rail.classList.remove('is-open');
    var overlay = qs('#sb-rail-overlay');
    if (overlay) overlay.classList.remove('is-open');
    if (!infoDrawer || !infoDrawer.classList.contains('is-open')) document.body.style.overflow = '';
  }

  function renderRail() {
    if (!railBody) return;

    var rows = buildRows();
    var html = '<div class="sb-rail__rows">';
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      html += '<div class="sb-rail__row' + (r.muted ? ' sb-rail__row--muted' : '') + '">' +
        '<div class="sb-rail__row-main">' +
          '<span class="sb-rail__label">' + escapeHtml(r.label) + '</span>' +
          '<span class="sb-rail__value">' + escapeHtml(r.value) + '</span>' +
          (r.detail ? '<span class="sb-rail__detail">' + escapeHtml(r.detail) + '</span>' : '') +
        '</div>' +
        '<div class="sb-rail__row-side">' +
          '<span class="sb-rail__price">' + escapeHtml(rowPriceText(r)) + '</span>' +
          '<button type="button" class="sb-rail__edit" data-sb-edit-step="' + r.step + '">Edit</button>' +
        '</div>' +
      '</div>';
    }
    html += '</div>';

    /* Fit line for the chosen enclosure */
    if (roomIsSet() && state.enclosureDims) {
      var fit = fitForEnclosure(state.enclosureDims, lmData());
      html += '<div class="sb-rail__fit">' + badgeHtml(fit) + '</div>';
    }

    /* Software estimate */
    var sw = softwareLine();
    if (sw && (sw.yr || sw.note)) {
      html += '<div class="sb-rail__software">' +
        '<span class="sb-rail__label">' + escapeHtml(railConfig.softwareLabel) + '</span>' +
        '<span class="sb-rail__value">' + (sw.yr ? 'from ' + formatMoneyShort(sw.yr) + escapeHtml(railConfig.softwareSuffix) : 'Not in total') + '</span>' +
        (sw.note ? '<span class="sb-rail__detail">' + escapeHtml(sw.note) + '</span>' : '') +
      '</div>';
    }

    /* Included / not included */
    html += '<div class="sb-rail__lists">';
    if (railConfig.included && railConfig.included.length) {
      html += '<div class="sb-rail__list"><span class="sb-rail__list-title">Included</span><ul>';
      for (var a = 0; a < railConfig.included.length; a++) html += '<li>' + escapeHtml(railConfig.included[a]) + '</li>';
      html += '</ul></div>';
    }
    if (railConfig.notIncluded && railConfig.notIncluded.length) {
      html += '<div class="sb-rail__list sb-rail__list--not"><span class="sb-rail__list-title">Not included</span><ul>';
      for (var b = 0; b < railConfig.notIncluded.length; b++) html += '<li>' + escapeHtml(railConfig.notIncluded[b]) + '</li>';
      html += '</ul></div>';
    }
    html += '</div>';

    /* Totals */
    var total = calculateTotalSilent();
    var savings = savingsFor(total);
    if (savings > 0) {
      html += '<div class="sb-rail__savings"><span>Bundle savings</span><span>-' + formatMoney(savings) + '</span></div>';
    }
    html += '<div class="sb-rail__total"><span>Total</span><span class="sb-rail__total-price">' + formatMoney(total) + '</span></div>';
    if (railConfig.trustLine) html += '<div class="sb-rail__trust">' + escapeHtml(railConfig.trustLine) + '</div>';

    railBody.innerHTML = html;
  }

  function copyText(text, cb) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function() { cb(true); }, function() { cb(legacyCopy(text)); });
    } else {
      cb(legacyCopy(text));
    }
  }

  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch(e) {
      return false;
    }
  }

  /* ═════════════════════════════════════════════
     URL + LOCAL STORAGE PERSISTENCE (new in v3)
     ?build=r:garage,dim:5x3x2.4,u:m,enc:3,scr:plat,mat:tee,
            proj:62008249811274,lm:53628241674570x1,acc:sideNetting,step:8
     ═════════════════════════════════════════════ */
  var STORAGE_KEY = 'sb_build_v3';
  var URL_PARAM = 'build';
  var persistTimer = null;

  function serialize() {
    var parts = [];
    if (state.roomType) parts.push('r:' + state.roomType);
    if (roomIsSet()) parts.push('dim:' + state.roomW + 'x' + state.roomD + 'x' + state.roomH);
    if (state.roomUnit !== 'm') parts.push('u:' + state.roomUnit);
    if (state.enclosureSize) parts.push('enc:' + state.enclosureSize.replace(/\D/g, ''));
    if (state.screenType !== 'standard') parts.push('scr:' + (state.screenType === 'platinum' ? 'plat' : state.screenType));
    if (state.matType !== 'none') parts.push('mat:' + (state.matType === 'tee-turf' ? 'tee' : 'std'));
    if (state.projectorVariantId) parts.push('proj:' + state.projectorVariantId);
    if (state.projectorVariantId && state.projectorMount === 'ceiling') parts.push('mnt:ceiling');
    if (state.launchMonitorVariantId) parts.push('lm:' + state.launchMonitorVariantId + (state.launchMonitorQty > 1 ? 'x' + state.launchMonitorQty : ''));
    var accKeys = Object.keys(state.accessories);
    var active = [];
    for (var i = 0; i < accKeys.length; i++) if (state.accessories[accKeys[i]].active) active.push(accKeys[i]);
    if (active.length) parts.push('acc:' + active.join('+'));
    if (state.currentStep > 1) parts.push('step:' + state.currentStep);
    return parts.join(',');
  }

  function parseBuild(str) {
    var out = {};
    if (!str) return out;
    var parts = str.split(',');
    for (var i = 0; i < parts.length; i++) {
      var idx = parts[i].indexOf(':');
      if (idx < 0) continue;
      out[parts[i].slice(0, idx)] = parts[i].slice(idx + 1);
    }
    return out;
  }

  function persist() {
    if (restoring || !state.touched) return;
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(writePersist, 120);
  }

  function writePersist() {
    if (!state.touched) return;
    var str = serialize();
    try {
      if (str) window.localStorage.setItem(STORAGE_KEY, str);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch(e) { /* private mode */ }

    try {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      if (str) url.searchParams.set(URL_PARAM, str);
      else url.searchParams['delete'](URL_PARAM);
      var next = url.pathname + url.search + url.hash;
      if (next !== window.location.pathname + window.location.search + window.location.hash) {
        window.history.replaceState(null, '', next);
      }
    } catch(e) { /* ignore */ }
  }

  function readSavedBuild() {
    var fromUrl = null;
    try {
      var url = new URL(window.location.href);
      fromUrl = url.searchParams.get(URL_PARAM);
    } catch(e) { /* ignore */ }
    if (fromUrl) return { source: 'url', data: parseBuild(fromUrl) };

    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) return { source: 'storage', data: parseBuild(stored) };
    } catch(e) { /* ignore */ }
    return null;
  }

  function clickCard(sel) {
    var el = qs(sel, root);
    if (el) el.click();
    return !!el;
  }

  function applyBuild(b) {
    restoring = true;
    try {
      if (b.u === 'ft') setUnit('ft');
      if (b.r) clickCard('[data-sb-room-type="' + b.r + '"]');
      if (b.dim) {
        var d = b.dim.split('x');
        if (d.length === 3) {
          state.roomW = parseFloat(d[0]) || null;
          state.roomD = parseFloat(d[1]) || null;
          state.roomH = parseFloat(d[2]) || null;
          syncRoomInputs();
        }
      }
      if (b.enc) clickCard('[data-sb-enclosure-card][data-sim-label="SIM ' + b.enc + '"]');
      if (b.scr) clickCard('[data-sb-screen-card][data-screen-type="' + (b.scr === 'plat' ? 'platinum' : b.scr) + '"]');
      if (b.mat) clickCard('[data-sb-mat-card][data-mat-type="' + (b.mat === 'tee' ? 'tee-turf' : 'standard') + '"]');
      if (b.mnt === 'ceiling') state.projectorMount = 'ceiling';
      if (b.proj) clickCard('[data-sb-projector-card][data-variant-id="' + b.proj + '"]');
      if (b.lm) {
        var lm = b.lm.split('x');
        if (clickCard('[data-sb-lm-card][data-variant-id="' + lm[0] + '"]') && lm[1]) setLaunchMonitorQty(lm[1]);
      }
      if (b.acc) {
        var keys = b.acc.split('+');
        for (var i = 0; i < keys.length; i++) {
          var card = qs('[data-sb-accessory-card][data-accessory-key="' + keys[i] + '"]', root);
          if (card && !state.accessories[keys[i]].active) card.click();
        }
      }
      refreshFitBadges();
      var step = parseInt(b.step, 10);
      if (step > 1 && step <= state.totalSteps) {
        state.maxStepReached = step;
        goToStep(step);
      }
    } catch(e) {
      console.error(LOG, 'Restore failed', e);
    }
    restoring = false;
    state.touched = true;
    calculateTotal();
    renderRail();
    writePersist();
  }

  function showRestoreNotice(source) {
    if (!layoutRight) return;
    var note = document.createElement('div');
    note.className = 'sb-restore-note';
    note.id = 'sb-restore-note';
    note.innerHTML = '<span>' + (source === 'url' ? 'We’ve loaded the build from your link.' : 'Welcome back. We’ve restored your last build.') + '</span>' +
      '<button type="button" class="sb-restore-note__btn" id="sb-start-over">Start over</button>';
    layoutRight.insertBefore(note, layoutRight.firstChild);
    var btn = qs('#sb-start-over', note);
    if (btn) {
      btn.addEventListener('click', function() {
        try { window.localStorage.removeItem(STORAGE_KEY); } catch(e) { /* ignore */ }
        try {
          var url = new URL(window.location.href);
          url.searchParams['delete'](URL_PARAM);
          window.location.href = url.pathname + url.search;
        } catch(e) {
          window.location.reload();
        }
      });
    }
  }

  /* ─────────────────────────────────────────────
     INFO DRAWER
     ───────────────────────────────────────────── */
  function renderInfoContent(data) {
    var html = '';
    var hasImage = data.image && typeof data.image === 'string' && data.image.length > 0;
    var cardClass = 'sb-info-card' + (hasImage ? ' sb-info-card--has-image' : '');

    html += '<div class="' + cardClass + '" style="animation-delay:0ms">';
    if (hasImage) {
      html += '<div class="sb-info-card__image-wrap">';
      html += '<img class="sb-info-card__image" src="' + escapeHtml(data.image) + '" alt="' + escapeHtml(data.title || '') + '" loading="lazy" onerror="this.parentNode.style.display=\'none\'">';
      html += '</div>';
    }
    html += '<div class="sb-info-card__body">';
    html += '<h4 class="sb-info-card__title">' + escapeHtml(data.title || '') + '</h4>';
    if (data.description) html += '<p class="sb-info-card__desc">' + escapeHtml(data.description) + '</p>';
    else if (data.body) html += '<p class="sb-info-card__desc">' + escapeHtml(data.body) + '</p>';

    if (data.specs && typeof data.specs === 'object') {
      var specKeys = Object.keys(data.specs);
      if (specKeys.length > 0) {
        html += '<div class="sb-info-card__specs">';
        for (var s = 0; s < specKeys.length; s++) {
          html += '<div class="sb-info-card__specs-row">';
          html += '<span class="sb-info-card__specs-label">' + escapeHtml(specKeys[s]) + '</span>';
          html += '<span class="sb-info-card__specs-value">' + escapeHtml(data.specs[specKeys[s]]) + '</span>';
          html += '</div>';
        }
        html += '</div>';
      }
    }
    html += '</div></div>';
    return html;
  }

  function openInfoDrawer(key) {
    if (!infoDrawer || !infoContent) return;
    var data = infoContent[key];
    if (!data) {
      console.warn(LOG, 'No info content for key:', key);
      return;
    }
    if (infoDrawerTitle) infoDrawerTitle.textContent = data.title || '';
    if (infoDrawerBody) infoDrawerBody.innerHTML = renderInfoContent(data);
    infoDrawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeInfoDrawer() {
    if (!infoDrawer) return;
    infoDrawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function initInfoDrawer() {
    root.addEventListener('click', function(e) {
      var infoBtn = e.target.closest('[data-sb-info]');
      if (infoBtn) {
        e.stopPropagation();
        e.preventDefault();
        openInfoDrawer(infoBtn.getAttribute('data-sb-info'));
        return;
      }
      if (e.target.closest('[data-sb-info-close]')) closeInfoDrawer();
    });

    /* Purchase-intent select (delegated — the summary re-renders its innerHTML) */
    root.addEventListener('change', function(e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-sb-timeline')) {
        state.timeline = e.target.value || null;
        try { window.localStorage.setItem('sb_timeline_v3', state.timeline || ''); } catch(err) {}
        console.log(LOG, 'Purchase timeline:', state.timeline);
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape') return;
      if (infoDrawer && infoDrawer.classList.contains('is-open')) closeInfoDrawer();
      else if (rail && rail.classList.contains('is-open')) closeRail();
    });
  }

  /* ─────────────────────────────────────────────
     CART SUBMISSION
     ───────────────────────────────────────────── */
  function addToCart() {
    if (!state.enclosureVariantId) {
      console.error(LOG, 'No enclosure variant resolved');
      return;
    }

    var props = { '_bundle': 'Home Golf Simulator Builder' };
    var buildStr = serialize();
    if (buildStr) props['_build'] = buildStr;
    if (roomIsSet()) props['Room size'] = roomDimsLabel();
    if (state.timeline) props['Purchase timeline'] = timelineLabel(state.timeline);

    var items = [];
    items.push({ id: state.enclosureVariantId, quantity: 1, properties: props });
    if (state.screenType !== 'standard' && state.screenVariantId) {
      items.push({ id: parseInt(state.screenVariantId, 10), quantity: 1, properties: { '_bundle': 'Home Golf Simulator Builder' } });
    }
    if (state.projectorVariantId) {
      items.push({ id: parseInt(state.projectorVariantId, 10), quantity: 1, properties: { '_bundle': 'Home Golf Simulator Builder' } });
      var mnt = currentMount();
      if (mnt) items.push({ id: parseInt(mnt.variantId, 10), quantity: 1, properties: { '_bundle': 'Home Golf Simulator Builder' } });
    }
    if (state.launchMonitorVariantId) {
      items.push({ id: parseInt(state.launchMonitorVariantId, 10), quantity: state.launchMonitorQty, properties: { '_bundle': 'Home Golf Simulator Builder' } });
    }
    var accKeys = Object.keys(state.accessories);
    for (var i = 0; i < accKeys.length; i++) {
      var acc = state.accessories[accKeys[i]];
      if (acc.active && acc.variantId) {
        items.push({ id: parseInt(acc.variantId, 10), quantity: 1, properties: { '_bundle': 'Home Golf Simulator Builder' } });
      }
    }

    console.log(LOG, 'Submitting', items.length, 'items to cart');
    console.log(LOG, 'Items:', JSON.stringify(items));

    if (atcBtn) atcBtn.disabled = true;
    if (atcText) atcText.textContent = 'Adding...';
    if (mobileNextBtn) { mobileNextBtn.disabled = true; mobileNextBtn.textContent = 'Adding...'; }

    try {
      var _learnq = window._learnq || [];
      _learnq.push(['track', 'Simulator Build Added To Cart', {
        'Total': formatMoney(calculateTotalSilent()),
        'Build': buildStr,
        'BuildURL': window.location.href,
        'RoomType': state.roomType || '',
        'RoomSize': roomDimsLabel(),
        'Timeline': state.timeline ? timelineLabel(state.timeline) : ''
      }]);
    } catch(e) { /* ignore */ }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(res) {
      if (!res.ok) {
        return res.json().then(function(data) {
          throw new Error(data.description || data.message || 'Cart error');
        });
      }
      return res.json();
    })
    .then(function() {
      console.log(LOG, 'Cart add success — redirecting');
      window.location.href = '/cart';
    })
    .catch(function(err) {
      console.error(LOG, 'Cart add failed:', err);
      alert('Could not add to cart: ' + err.message);
      if (atcBtn) atcBtn.disabled = false;
      if (atcText) atcText.textContent = 'Add to Cart';
      if (mobileNextBtn) { mobileNextBtn.disabled = false; mobileNextBtn.textContent = 'Add to Cart'; }
    });
  }

  /* ─────────────────────────────────────────────
     HELP TOGGLES
     ───────────────────────────────────────────── */
  function initHelpToggles() {
    var toggles = qsa('[data-sb-help-toggle]', root);
    for (var i = 0; i < toggles.length; i++) {
      (function(toggle) {
        toggle.addEventListener('click', function() {
          var expand = toggle.nextElementSibling;
          if (!expand) return;
          var isOpen = expand.classList.contains('is-open');
          expand.classList.toggle('is-open', !isOpen);
          toggle.classList.toggle('is-open', !isOpen);
        });
      })(toggles[i]);
    }
  }

  /* ─────────────────────────────────────────────
     NAVIGATION BUTTONS
     ───────────────────────────────────────────── */
  function initNavigation() {
    var nextBtns = qsa('[data-sb-next]', root);
    for (var i = 0; i < nextBtns.length; i++) {
      nextBtns[i].addEventListener('click', function() {
        if (state.currentStep < state.totalSteps) goToStep(state.currentStep + 1);
      });
    }

    var backBtns = qsa('[data-sb-back]', root);
    for (var j = 0; j < backBtns.length; j++) {
      backBtns[j].addEventListener('click', function() {
        if (state.currentStep > 1) goToStep(state.currentStep - 1);
      });
    }

    if (mobileNextBtn) {
      mobileNextBtn.addEventListener('click', function() {
        if (state.currentStep === state.totalSteps) addToCart();
        else goToStep(state.currentStep + 1);
      });
    }

    if (atcBtn) atcBtn.addEventListener('click', addToCart);

    if (summaryItems) {
      summaryItems.addEventListener('click', function(e) {
        var editBtn = e.target.closest('[data-sb-edit-step]');
        if (editBtn) {
          var step = parseInt(editBtn.getAttribute('data-sb-edit-step'), 10);
          if (step) goToStep(step);
        }
      });
    }

    /* Selection chips (delegated) */
    if (selectionsInner) {
      selectionsInner.addEventListener('click', function(e) {
        var chip = e.target.closest('[data-sb-edit-step]');
        if (chip) {
          var step = parseInt(chip.getAttribute('data-sb-edit-step'), 10);
          if (step) goToStep(step);
        }
      });
    }

    /* Completed progress circles are clickable */
    var progress = qs('#sb-progress');
    if (progress) {
      progress.addEventListener('click', function(e) {
        var node = e.target.closest('[data-sb-progress-step]');
        if (!node) return;
        var step = parseInt(node.getAttribute('data-sb-progress-step'), 10);
        if (step && step <= state.maxStepReached) goToStep(step);
      });
    }
  }

  function initMobileBarExtras() {
    if (!mobileBar) return;
    var inner = qs('.sb-mobile-bar__inner', mobileBar);
    if (!inner) return;

    var back = document.createElement('button');
    back.type = 'button';
    back.className = 'sb-mobile-bar__back';
    back.id = 'sb-mobile-back';
    back.setAttribute('aria-label', 'Back');
    back.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>';
    back.style.display = 'none';
    back.addEventListener('click', function() {
      if (state.currentStep > 1) goToStep(state.currentStep - 1);
    });
    inner.insertBefore(back, inner.firstChild);

    var prices = qs('.sb-mobile-bar__prices', mobileBar);
    if (prices) {
      prices.classList.add('sb-mobile-bar__prices--tappable');
      prices.setAttribute('role', 'button');
      prices.setAttribute('tabindex', '0');
      prices.setAttribute('aria-label', 'Open build summary');
      var caret = document.createElement('span');
      caret.className = 'sb-mobile-bar__summary-link';
      caret.textContent = 'Summary';
      prices.appendChild(caret);
      prices.addEventListener('click', function() {
        if (rail && rail.classList.contains('is-open')) closeRail(); else openRail();
      });
    }
  }

  /* ─────────────────────────────────────────────
     MOBILE: hero image clone in each step
     ───────────────────────────────────────────── */
  function initMobileImages() {
    if (window.innerWidth >= 900) return;
    var steps = qsa('.sb-step', root);
    for (var i = 0; i < steps.length; i++) {
      var stepNum = parseInt(steps[i].getAttribute('data-sb-step'), 10);
      if (stepNum === STEP.summary || stepNum === STEP.room) continue;
      var mobileImg = document.createElement('div');
      mobileImg.className = 'sb-step__mobile-image';
      mobileImg.innerHTML = '<img src="' + (heroImage ? heroImage.src : '') + '" alt="SimSpace" width="800" height="800" loading="lazy">';
      steps[i].insertBefore(mobileImg, steps[i].firstChild);
    }
  }

  /* ─────────────────────────────────────────────
     FREE DESIGN CONSULTATION CTA (v3.2)
     Replaces the old "Need help choosing?" toggle on every step with an
     animated pro-cage-style banner. The WhatsApp link picks up the current
     ?build= URL at click time so Tom opens the visitor's exact build.
     ───────────────────────────────────────────── */
  function consultCtaHtml() {
    var waIcon = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.6-6.1c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.7.8-.8 1-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.4-3c-.3-.4 0-.5.1-.7l.4-.5c.1-.2.1-.3.2-.5 0-.2 0-.4-.1-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1.1 2.7c.1.2 1.8 2.8 4.5 3.9.6.3 1.1.4 1.5.6.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.6-.3z"/></svg>';
    return '' +
      '<div class="sb-consult" data-sb-consult>' +
        '<div class="sb-consult__eyebrow">' +
          '<span class="sb-consult__dot"></span>' +
          '<span class="sb-consult__chip">FREE</span>' +
          '<span class="sb-consult__eyebrow-text">Design consultation &amp; bespoke quote</span>' +
        '</div>' +
        '<p class="sb-consult__title">Not sure what fits? Design it with one of our sim experts.</p>' +
        '<p class="sb-consult__text">Send our experts your build and they’ll design your simulator around your room and budget — a free consultation and a bespoke quote, with options the builder can’t show.</p>' +
        '<div class="sb-consult__ctas">' +
          '<a class="sb-consult__wa" data-sb-consult-wa href="#" target="_blank" rel="noopener">' + waIcon + 'WhatsApp an expert my build</a>' +
          '<a class="sb-consult__phone" href="tel:+442033768500">or call 02033 768500</a>' +
        '</div>' +
        '<div class="sb-consult__trust"><span class="sb-consult__stars">★★★★★</span>Trusted by <b>1,000s</b> of UK golfers · No obligation</div>' +
      '</div>';
  }

  function initConsultCTA() {
    var helpBlocks = qsa('.sb-help-link', root);
    var i;
    if (helpBlocks.length) {
      for (i = 0; i < helpBlocks.length; i++) {
        var holder = document.createElement('div');
        holder.innerHTML = consultCtaHtml();
        helpBlocks[i].parentNode.replaceChild(holder.firstChild, helpBlocks[i]);
      }
    } else {
      var navs = qsa('.sb-step .sb-nav', root);
      for (i = 0; i < navs.length; i++) {
        var wrap = document.createElement('div');
        wrap.innerHTML = consultCtaHtml();
        navs[i].parentNode.insertBefore(wrap.firstChild, navs[i]);
      }
    }

    var links = qsa('[data-sb-consult-wa]', root);
    for (i = 0; i < links.length; i++) {
      (function(link) {
        function setHref() {
          var tl = state.timeline ? ' My timeline: ' + timelineLabel(state.timeline) + '.' : '';
          var msg = 'Hi — I’m using the SimSpace Builder and would like a free design consultation and bespoke quote.' + tl + ' My build so far: ' + window.location.href;
          link.href = (railConfig.whatsappUrl || 'https://wa.me/447717305356') + '?text=' + encodeURIComponent(msg);
        }
        link.addEventListener('mousedown', setHref);
        link.addEventListener('touchstart', setHref);
        link.addEventListener('click', function() {
          setHref();
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'whatsapp_consult_click', { location: 'sim_builder', step: state.currentStep });
          }
        });
      })(links[i]);
    }
    console.log(LOG, 'Consult CTA on', Math.max(helpBlocks.length, qsa('[data-sb-consult]', root).length), 'steps');
  }

  /* ─────────────────────────────────────────────
     PDF QUOTE (print-to-PDF, no library — borrowed from
     the Full Swing builder's "Download your quote")
     ───────────────────────────────────────────── */
  function buildQuoteHtml() {
    var rows = buildRows();
    var total = calculateTotal();
    var savings = savingsFor(total);
    var itemsHtml = '';
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var nm = r.label === 'Room' ? 'Room' : (r.label + ' — ' + r.value);
      var dt = r.label === 'Room' ? r.value : (r.detail || '');
      itemsHtml += '<tr><td>' + escapeHtml(nm) +
        (dt ? '<br><span class="q-detail">' + escapeHtml(dt) + '</span>' : '') +
        '</td><td class="q-price">' + escapeHtml(rowPriceText(r)) + '</td></tr>';
    }
    var sw = softwareLine();
    if (sw && (sw.yr || sw.note)) {
      itemsHtml += '<tr><td>' + escapeHtml(railConfig.softwareLabel) +
        (sw.note ? '<br><span class="q-detail">' + escapeHtml(sw.note) + '</span>' : '') +
        '</td><td class="q-price">' + (sw.yr ? 'from ' + escapeHtml(formatMoneyShort(sw.yr)) + escapeHtml(railConfig.softwareSuffix || '') : 'Not in total') + '</td></tr>';
    }
    var d = new Date();
    var dateStr = d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear();
    var tlHtml = state.timeline ? '<p class="q-meta">Purchase timeline: ' + escapeHtml(timelineLabel(state.timeline)) + '</p>' : '';
    return '<div class="q-head"><div class="q-brand">OpenGolfer</div>' +
        '<div class="q-sub">Home Golf Simulator — your build quote</div>' +
        '<div class="q-date">' + escapeHtml(dateStr) + '</div></div>' +
      '<table class="q-table"><tbody>' + itemsHtml + '</tbody>' +
      '<tfoot>' +
        (savings > 0 ? '<tr class="q-savings"><td>Bundle savings</td><td class="q-price">-' + escapeHtml(formatMoney(savings)) + '</td></tr>' : '') +
        '<tr class="q-total"><td>Total (inc. VAT)</td><td class="q-price">' + escapeHtml(formatMoney(total)) + '</td></tr>' +
      '</tfoot></table>' +
      tlHtml +
      '<p class="q-meta">Edit or share this build: ' + escapeHtml(window.location.href) + '</p>' +
      '<div class="q-foot"><strong>Questions?</strong> WhatsApp 07717 305356 · Call 02033 768500 · hello@opengolfer.co.uk<br>' +
      'Prices include VAT. Quote for guidance — final availability confirmed at checkout.</div>';
  }

  function printQuote() {
    var node = document.getElementById('sb-print-quote');
    if (!node) {
      node = document.createElement('div');
      node.id = 'sb-print-quote';
      document.body.appendChild(node);
    }
    node.innerHTML = buildQuoteHtml();
    var htmlEl = document.documentElement;
    htmlEl.classList.add('sb-printing');
    function cleanup() {
      htmlEl.classList.remove('sb-printing');
      window.removeEventListener('afterprint', cleanup);
    }
    window.addEventListener('afterprint', cleanup);
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'sim_builder_quote_download', { location: 'sim_builder' });
    }
    setTimeout(function() { window.print(); setTimeout(cleanup, 2000); }, 60);
    console.log(LOG, 'Quote print/download triggered');
  }

  /* ─────────────────────────────────────────────
     EMAIL QUOTE
     ───────────────────────────────────────────── */
  function initEmailQuote() {
    var toggle = qs('#sb-email-quote-toggle');
    var form = qs('#sb-email-quote-form');
    var input = qs('#sb-email-quote-input');
    var submit = qs('#sb-email-quote-submit');
    var status = qs('#sb-email-quote-status');
    if (!toggle || !form) return;

    /* Inject the "Download quote (PDF)" button next to "Email me this quote" */
    if (!document.querySelector('[data-sb-download-quote]')) {
      var dlBtn = document.createElement('button');
      dlBtn.type = 'button';
      dlBtn.className = 'sb-quote-download';
      dlBtn.setAttribute('data-sb-download-quote', '');
      dlBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16"/></svg> Download quote (PDF)';
      dlBtn.addEventListener('click', printQuote);
      if (toggle.parentNode) toggle.parentNode.insertBefore(dlBtn, toggle.nextSibling);
    }

    toggle.addEventListener('click', function() {
      var isVisible = form.style.display !== 'none';
      form.style.display = isVisible ? 'none' : 'flex';
      if (!isVisible && input) input.focus();
    });

    if (!submit) return;
    submit.addEventListener('click', function() {
      if (!input || !input.value || !input.validity.valid) {
        if (input) input.focus();
        return;
      }

      writePersist();
      var email = input.value.trim();
      var total = calculateTotal();
      var rows = buildRows();
      var lines = [];
      var quoteItems = [];

      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        lines.push(r.label + ': ' + r.value + ' — ' + rowPriceText(r));
        quoteItems.push({ name: r.value, category: r.label, price: rowPriceText(r) });
      }

      var sw = softwareLine();
      if (sw && (sw.yr || sw.note)) {
        lines.push(railConfig.softwareLabel + ': ' + (sw.yr ? 'from ' + formatMoneyShort(sw.yr) + ' per year (estimate, not in total)' : 'not in total') + (sw.note ? '. ' + sw.note : ''));
      }

      var savings = savingsFor(total);
      var savingsText = savings > 0 ? '\nBundle Savings: -' + formatMoney(savings) : '';

      var body = 'Home Golf Simulator Builder Quote\n' +
        '================================\n\n' +
        lines.join('\n') +
        savingsText +
        '\n\nTotal: ' + formatMoney(total) +
        '\n\n---\nView and edit your build: ' + window.location.href;

      submit.disabled = true;
      submit.textContent = 'Sending...';
      if (status) { status.textContent = ''; status.className = 'sb-email-quote__status'; }

      try {
        var _learnq = window._learnq || [];
        _learnq.push(['identify', { '$email': email }]);
        _learnq.push(['track', 'Simulator Quote Requested', {
          'QuoteTotal': formatMoney(total),
          'QuoteTotalCents': total,
          'QuoteItems': quoteItems,
          'QuoteItemsText': body,
          'Savings': savings > 0 ? formatMoney(savings) : '',
          'EnclosureSize': state.enclosureSize || '',
          'RoomType': state.roomType || '',
          'RoomSize': roomDimsLabel(),
          'Build': serialize(),
          'QuoteURL': window.location.href
        }]);
        console.log(LOG, 'Klaviyo event tracked: Simulator Quote Requested for', email);
      } catch(klErr) {
        console.error(LOG, 'Klaviyo tracking error:', klErr);
      }

      var formData = new FormData();
      formData.append('form_type', 'contact');
      formData.append('utf8', '✓');
      formData.append('contact[email]', email);
      formData.append('contact[body]', body);
      formData.append('contact[tags]', 'simulator-quote');
      formData.append('contact[subject]', 'Simulator Builder Quote — ' + formatMoney(total));

      function done() {
        submit.textContent = 'Sent!';
        if (status) {
          status.textContent = 'Quote sent to ' + email;
          status.className = 'sb-email-quote__status sb-email-quote__status--success';
        }
        setTimeout(function() {
          submit.disabled = false;
          submit.textContent = 'Send Quote';
        }, 3000);
      }

      fetch('/contact', { method: 'POST', headers: { 'Accept': 'application/json' }, body: formData })
        .then(function() { console.log(LOG, 'Quote sent to', email); done(); })
        .catch(function(err) { console.error(LOG, 'Contact form error:', err); done(); });
    });
  }

  /* ─────────────────────────────────────────────
     INIT
     ───────────────────────────────────────────── */
  function init() {
    console.log(LOG, 'Initializing...');

    injectRoomStep();
    initRail();
    initMobileBarExtras();

    /* Mark the build as "touched" on the first real interaction (capture phase,
       so it runs before the card handlers call persist()). */
    root.addEventListener('click', function(e) {
      if (e.target.closest('.sb-card, [data-sb-next], [data-sb-back], [data-sb-unit], [data-sb-edit-step], [data-sb-lm-qty-minus], [data-sb-lm-qty-plus]')) {
        state.touched = true;
      }
    }, true);
    root.addEventListener('input', function(e) {
      if (e.target.closest('[data-sb-dim]')) state.touched = true;
    }, true);

    initRoomStep();
    initEnclosureCards();
    initScreenCards();
    initMatCards();
    initProjectorCards();
    initLaunchMonitorCards();
    initAccessoryCards();
    initHelpToggles();
    initNavigation();
    initMobileImages();
    initConsultCTA();
    initInfoDrawer();
    initEmailQuote();

    updateScreenPricesForSize();
    updateMatPricesForSize();
    resolveEnclosureVariant();
    refreshFitBadges();

    /* Restore a saved build before the first render */
    var saved = readSavedBuild();
    if (saved && Object.keys(saved.data).length) {
      console.log(LOG, 'Restoring build from', saved.source, saved.data);
      applyBuild(saved.data);
      showRestoreNotice(saved.source);
    }

    calculateTotal();

    var firstStep = qs('[data-sb-step="' + state.currentStep + '"]', root);
    if (firstStep) {
      firstStep.classList.add('is-active');
      requestAnimationFrame(function() {
        firstStep.classList.add('is-visible');
      });
    }

    updateProgressBar();
    updateMobileBar();
    renderRail();

    console.log(LOG, '════════════════════════════════════════════════');
    console.log(LOG, 'SIMSPACE BUILDER READY (v' + VERSION + ')');
    console.log(LOG, '════════════════════════════════════════════════');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(init, 300);
    });
  } else {
    setTimeout(init, 300);
  }

})();
